// ============================================================================
//  ShapeBuilder.js
//  ---------------------------------------------------------------------------
//  A standalone module that owns EVERYTHING shape-related:
//     • Parametric garment templates (tee_front, tee_back, sleeve, pant_front)
//     • Live SVG shape preview with measurement dimensions
//     • Spec-sheet import (CSV / JSON / paste)
//     • Seam-allowance offset engine  (spec sheet -> cut pattern with SA)
//     • AI Part Decomposer            (upload garment sketch -> parts list)
//     • AI Training Data Uploader
//     • Garment Assembly canvas       (combine parts back into full garment)
//     • Spread / Marker Layout tool   (2D bin-packing, yield, PDF/PNG export)
//
//  PartDNA.js imports { SpecDrivenShape } from here for its inline
//  "Spec Builder" tab, and imports the default ShapeBuilder component for the
//  full-page advanced view.
// ============================================================================

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Save, Trash2, X, Plus, Printer, Upload, Download, Wand2,
  Scissors, Grid, ZoomIn, ZoomOut, Image as ImageIcon, Maximize2,
  Minimize2, FileText, Eye, Edit2, Layers, Box, Brain, Package,
  Ruler, FolderOpen, CheckCircle2, AlertCircle
} from 'lucide-react';

// ============================================================================
//  API CONFIG
// ----------------------------------------------------------------------------
//  Every backend call is centralised here so the customer can point the three
//  AI endpoints at their own PHP / Node / Python handlers without hunting.
// ============================================================================
export const API_BASE = 'https://nirvikalp.integrashion.com/api';
export const API = {
  saveUniversal:      `${API_BASE}/save_universal.php`,
  aiShapeInterpret:   `${API_BASE}/ai_shape.php`,       // POST baseline+target -> {image|svg}
  aiDecomposeGarment: `${API_BASE}/ai_decompose.php`,   // POST sketch image  -> {parts:[{name, template, specs}]}
  aiTrainingUpload:   `${API_BASE}/ai_training.php`,    // POST training triples (sketch, parts, specs)
  listTrainingData:   `${API_BASE}/ai_training_list.php`,
};

// ============================================================================
//  GARMENT TEMPLATES
// ----------------------------------------------------------------------------
//  Each template = pure function:  measurements -> { path, polyline, dims }
//  path     : SVG "d" attribute (y-up world coords, origin at bottom-center)
//  polyline : densely sampled [[x,y], …] – used by SA offset + bin packing
//  dims     : dimension annotations for the preview
//  width/height: bounding-box dimensions (in cm, same unit as measurements)
// ============================================================================
const M_ = (x, y)         => `M ${x.toFixed(2)} ${y.toFixed(2)}`;
const L_ = (x, y)         => `L ${x.toFixed(2)} ${y.toFixed(2)}`;
const Q_ = (cx, cy, x, y) => `Q ${cx.toFixed(2)} ${cy.toFixed(2)} ${x.toFixed(2)} ${y.toFixed(2)}`;

// Sample a quadratic bezier into N points (inclusive of end, exclusive of start so concat works)
const sampleQuad = (x0, y0, cx, cy, x1, y1, n = 24) => {
  const pts = [];
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    const mt = 1 - t;
    pts.push([
      mt * mt * x0 + 2 * mt * t * cx + t * t * x1,
      mt * mt * y0 + 2 * mt * t * cy + t * t * y1,
    ]);
  }
  return pts;
};

export const GARMENT_TEMPLATES = {
  // -------------------------- TEE / SWEATER FRONT --------------------------
  tee_front: {
    name: 'Tee / Sweater – Front',
    icon: '👕',
    measurements: {
      hemWidth:       { label: 'Hem / Bottom Width',         default: 52, unit: 'cm', group: 'Width',    min: 10, max: 200 },
      chestWidth:     { label: 'Chest Width',                default: 54, unit: 'cm', group: 'Width',    min: 10, max: 200 },
      shoulderWidth:  { label: 'Shoulder Width',             default: 44, unit: 'cm', group: 'Shoulder', min: 10, max: 100 },
      shoulderSlope:  { label: 'Shoulder Slope Drop',        default: 3,  unit: 'cm', group: 'Shoulder', min: 0,  max: 15  },
      bodyLength:     { label: 'Body Length (hem→armpit)',   default: 45, unit: 'cm', group: 'Length',   min: 5,  max: 150 },
      armholeDrop:    { label: 'Armhole Drop',               default: 25, unit: 'cm', group: 'Armhole',  min: 5,  max: 50  },
      neckWidth:      { label: 'Neck Opening Width',         default: 18, unit: 'cm', group: 'Neck',     min: 4,  max: 40  },
      neckDepthFront: { label: 'Front Neck Depth',           default: 8,  unit: 'cm', group: 'Neck',     min: 1,  max: 30  },
    },
    build: (m) => {
      const hemH  = m.hemWidth / 2,  chstH = m.chestWidth / 2;
      const shldH = m.shoulderWidth / 2, neckH = m.neckWidth / 2;
      const Ltot  = m.bodyLength + m.armholeDrop;
      const shldY = Ltot - m.shoulderSlope, armY = m.bodyLength;
      const neckBotY = Ltot - m.neckDepthFront;

      const path = [
        M_(-hemH, 0), L_(hemH, 0), L_(chstH, armY),
        Q_(chstH * 0.95, armY + (shldY - armY) * 0.55, shldH, shldY),
        L_(neckH, Ltot),
        Q_(neckH * 0.55, neckBotY - 0.5, 0, neckBotY),
        Q_(-neckH * 0.55, neckBotY - 0.5, -neckH, Ltot),
        L_(-shldH, shldY),
        Q_(-chstH * 0.95, armY + (shldY - armY) * 0.55, -chstH, armY),
        L_(-hemH, 0), 'Z',
      ].join(' ');

      // Sample polyline (follow the same vertex sequence)
      const polyline = [
        [-hemH, 0], [hemH, 0], [chstH, armY],
        ...sampleQuad(chstH, armY, chstH * 0.95, armY + (shldY - armY) * 0.55, shldH, shldY),
        [neckH, Ltot],
        ...sampleQuad(neckH, Ltot, neckH * 0.55, neckBotY - 0.5, 0, neckBotY),
        ...sampleQuad(0, neckBotY, -neckH * 0.55, neckBotY - 0.5, -neckH, Ltot),
        [-shldH, shldY],
        ...sampleQuad(-shldH, shldY, -chstH * 0.95, armY + (shldY - armY) * 0.55, -chstH, armY),
        [-hemH, 0],
      ];

      const dims = [
        { kind: 'h', y: -4,       x1: -hemH,  x2: hemH,  label: `Hem ${m.hemWidth}` },
        { kind: 'h', y: armY - 3, x1: -chstH, x2: chstH, label: `Chest ${m.chestWidth}` },
        { kind: 'h', y: Ltot + 4, x1: -shldH, x2: shldH, label: `Shoulder ${m.shoulderWidth}` },
        { kind: 'h', y: Ltot + 9, x1: -neckH, x2: neckH, label: `Neck ${m.neckWidth}` },
        { kind: 'v', x: Math.max(hemH, chstH) + 4, y1: 0, y2: Ltot, label: `L ${Ltot.toFixed(1)}` },
      ];
      return { path, polyline, dims, width: Math.max(hemH, chstH, shldH) * 2 + 20, height: Ltot + 20 };
    },
  },

  // -------------------------- TEE / SWEATER BACK ---------------------------
  tee_back: {
    name: 'Tee / Sweater – Back',
    icon: '👕',
    measurements: {
      hemWidth:       { label: 'Hem Width',        default: 52, unit: 'cm', group: 'Width',    min: 10, max: 200 },
      chestWidth:     { label: 'Chest Width',      default: 54, unit: 'cm', group: 'Width',    min: 10, max: 200 },
      shoulderWidth:  { label: 'Shoulder Width',   default: 44, unit: 'cm', group: 'Shoulder', min: 10, max: 100 },
      shoulderSlope:  { label: 'Shoulder Slope',   default: 3,  unit: 'cm', group: 'Shoulder', min: 0,  max: 15  },
      bodyLength:     { label: 'Body Length',      default: 45, unit: 'cm', group: 'Length',   min: 5,  max: 150 },
      armholeDrop:    { label: 'Armhole Drop',     default: 25, unit: 'cm', group: 'Armhole',  min: 5,  max: 50  },
      neckWidth:      { label: 'Neck Width',       default: 18, unit: 'cm', group: 'Neck',     min: 4,  max: 40  },
      neckDepthBack:  { label: 'Back Neck Depth',  default: 2,  unit: 'cm', group: 'Neck',     min: 0.5,max: 10  },
    },
    build: (m) => {
      const hemH = m.hemWidth/2, chstH = m.chestWidth/2;
      const shldH = m.shoulderWidth/2, neckH = m.neckWidth/2;
      const Ltot = m.bodyLength + m.armholeDrop;
      const shldY = Ltot - m.shoulderSlope, armY = m.bodyLength;
      const neckBotY = Ltot - m.neckDepthBack;

      const path = [
        M_(-hemH, 0), L_(hemH, 0), L_(chstH, armY),
        Q_(chstH * 0.95, armY + (shldY - armY) * 0.55, shldH, shldY),
        L_(neckH, Ltot),
        Q_(neckH * 0.55, neckBotY - 0.2, 0, neckBotY),
        Q_(-neckH * 0.55, neckBotY - 0.2, -neckH, Ltot),
        L_(-shldH, shldY),
        Q_(-chstH * 0.95, armY + (shldY - armY) * 0.55, -chstH, armY),
        L_(-hemH, 0), 'Z',
      ].join(' ');

      const polyline = [
        [-hemH, 0], [hemH, 0], [chstH, armY],
        ...sampleQuad(chstH, armY, chstH * 0.95, armY + (shldY - armY) * 0.55, shldH, shldY),
        [neckH, Ltot],
        ...sampleQuad(neckH, Ltot, neckH * 0.55, neckBotY - 0.2, 0, neckBotY),
        ...sampleQuad(0, neckBotY, -neckH * 0.55, neckBotY - 0.2, -neckH, Ltot),
        [-shldH, shldY],
        ...sampleQuad(-shldH, shldY, -chstH * 0.95, armY + (shldY - armY) * 0.55, -chstH, armY),
        [-hemH, 0],
      ];

      const dims = [
        { kind: 'h', y: -4,       x1: -hemH,  x2: hemH,  label: `Hem ${m.hemWidth}` },
        { kind: 'h', y: armY - 3, x1: -chstH, x2: chstH, label: `Chest ${m.chestWidth}` },
        { kind: 'h', y: Ltot + 4, x1: -shldH, x2: shldH, label: `Shoulder ${m.shoulderWidth}` },
        { kind: 'v', x: Math.max(hemH, chstH) + 4, y1: 0, y2: Ltot, label: `L ${Ltot.toFixed(1)}` },
      ];
      return { path, polyline, dims, width: Math.max(hemH, chstH, shldH) * 2 + 20, height: Ltot + 20 };
    },
  },

  // -------------------------- SLEEVE ---------------------------------------
  sleeve: {
    name: 'Sleeve',
    icon: '🧥',
    measurements: {
      cuffWidth:    { label: 'Cuff Width',    default: 20, unit: 'cm', group: 'Width',  min: 4, max: 60  },
      bicepWidth:   { label: 'Bicep Width',   default: 38, unit: 'cm', group: 'Width',  min: 10, max: 100 },
      sleeveLength: { label: 'Sleeve Length', default: 56, unit: 'cm', group: 'Length', min: 5, max: 100 },
      capHeight:    { label: 'Cap Height',    default: 14, unit: 'cm', group: 'Cap',    min: 2, max: 30  },
      capWidth:     { label: 'Cap Top Flat',  default: 2,  unit: 'cm', group: 'Cap',    min: 0, max: 15  },
    },
    build: (m) => {
      const cuffH = m.cuffWidth/2, bicepH = m.bicepWidth/2, capH = m.capWidth/2;
      const shaftLen = m.sleeveLength - m.capHeight, Ltot = m.sleeveLength;

      const path = [
        M_(-cuffH, 0), L_(cuffH, 0), L_(bicepH, shaftLen),
        Q_(bicepH * 0.9, shaftLen + m.capHeight * 0.4, capH, Ltot),
        L_(-capH, Ltot),
        Q_(-bicepH * 0.9, shaftLen + m.capHeight * 0.4, -bicepH, shaftLen),
        L_(-cuffH, 0), 'Z',
      ].join(' ');

      const polyline = [
        [-cuffH, 0], [cuffH, 0], [bicepH, shaftLen],
        ...sampleQuad(bicepH, shaftLen, bicepH * 0.9, shaftLen + m.capHeight * 0.4, capH, Ltot),
        [-capH, Ltot],
        ...sampleQuad(-capH, Ltot, -bicepH * 0.9, shaftLen + m.capHeight * 0.4, -bicepH, shaftLen),
        [-cuffH, 0],
      ];

      const dims = [
        { kind: 'h', y: -4,           x1: -cuffH, x2: cuffH, label: `Cuff ${m.cuffWidth}` },
        { kind: 'h', y: shaftLen - 3, x1: -bicepH,x2: bicepH, label: `Bicep ${m.bicepWidth}` },
        { kind: 'v', x: bicepH + 4, y1: 0, y2: Ltot, label: `L ${m.sleeveLength}` },
      ];
      return { path, polyline, dims, width: bicepH * 2 + 20, height: Ltot + 20 };
    },
  },

  // -------------------------- PANT FRONT -----------------------------------
  pant_front: {
    name: 'Pant / Trouser – Front',
    icon: '👖',
    measurements: {
      waistWidth:   { label: 'Waist (½)',    default: 38, unit: 'cm', group: 'Width',  min: 10, max: 80 },
      hipWidth:     { label: 'Hip (½)',      default: 46, unit: 'cm', group: 'Width',  min: 10, max: 90 },
      thighWidth:   { label: 'Thigh (½)',    default: 30, unit: 'cm', group: 'Width',  min: 8, max: 70 },
      hemWidth:     { label: 'Hem / Ankle',  default: 18, unit: 'cm', group: 'Width',  min: 4, max: 60 },
      riseLength:   { label: 'Rise',         default: 26, unit: 'cm', group: 'Length', min: 5, max: 60 },
      inseamLength: { label: 'Inseam',       default: 76, unit: 'cm', group: 'Length', min: 10, max: 130 },
      hipDrop:      { label: 'Hip Line Drop',default: 18, unit: 'cm', group: 'Length', min: 2, max: 40 },
    },
    build: (m) => {
      const wH = m.waistWidth/2, hH = m.hipWidth/2, tH = m.thighWidth/2, hemH = m.hemWidth/2;
      const Ltot = m.riseLength + m.inseamLength;
      const crotchY = m.inseamLength, hipYabs = Ltot - m.hipDrop;

      const path = [
        M_(-hemH, 0), L_(hemH, 0), L_(tH, crotchY),
        Q_(hH * 1.02, (crotchY + hipYabs) / 2, hH, hipYabs),
        L_(wH, Ltot), L_(-wH * 0.15, Ltot),
        Q_(-wH * 0.4, Ltot - m.riseLength * 0.4, -wH * 0.55, Ltot - m.riseLength * 0.75),
        Q_(-wH * 0.9, crotchY + 2, -tH, crotchY),
        L_(-hemH, 0), 'Z',
      ].join(' ');

      const polyline = [
        [-hemH, 0], [hemH, 0], [tH, crotchY],
        ...sampleQuad(tH, crotchY, hH * 1.02, (crotchY + hipYabs) / 2, hH, hipYabs),
        [wH, Ltot], [-wH * 0.15, Ltot],
        ...sampleQuad(-wH * 0.15, Ltot, -wH * 0.4, Ltot - m.riseLength * 0.4, -wH * 0.55, Ltot - m.riseLength * 0.75),
        ...sampleQuad(-wH * 0.55, Ltot - m.riseLength * 0.75, -wH * 0.9, crotchY + 2, -tH, crotchY),
        [-hemH, 0],
      ];

      const dims = [
        { kind: 'h', y: -4,          x1: -hemH, x2: hemH, label: `Hem ${m.hemWidth}` },
        { kind: 'h', y: crotchY - 3, x1: -tH,   x2: tH,   label: `Thigh ${m.thighWidth}` },
        { kind: 'h', y: Ltot + 4,    x1: -wH*0.15, x2: wH,label: `Waist ${m.waistWidth}` },
        { kind: 'v', x: hH + 4, y1: 0, y2: Ltot, label: `L ${Ltot.toFixed(1)}` },
      ];
      return { path, polyline, dims, width: hH * 2 + 30, height: Ltot + 20 };
    },
  },

  // -------------------------- GENERIC RECT ---------------------------------
  rect_panel: {
    name: 'Generic Rectangle',
    icon: '⬜',
    measurements: {
      width:  { label: 'Width',  default: 40, unit: 'cm', group: 'Width',  min: 1, max: 300 },
      height: { label: 'Height', default: 60, unit: 'cm', group: 'Length', min: 1, max: 300 },
    },
    build: (m) => {
      const w = m.width, h = m.height;
      const path = `M ${-w/2} 0 L ${w/2} 0 L ${w/2} ${h} L ${-w/2} ${h} Z`;
      const polyline = [[-w/2, 0], [w/2, 0], [w/2, h], [-w/2, h], [-w/2, 0]];
      const dims = [
        { kind: 'h', y: -4,    x1: -w/2, x2: w/2, label: `W ${w}` },
        { kind: 'v', x: w/2 + 4, y1: 0, y2: h, label: `H ${h}` },
      ];
      return { path, polyline, dims, width: w + 20, height: h + 20 };
    },
  },

  // -------------------------- CUFF -----------------------------------------
  cuff: {
    name: 'Cuff',
    icon: '🔗',
    measurements: {
      cuffLength:  { label: 'Length (around wrist)', default: 22, unit: 'cm', group: 'Width',  min: 4, max: 40 },
      cuffHeight:  { label: 'Height',                default: 6,  unit: 'cm', group: 'Length', min: 2, max: 20 },
    },
    build: (m) => {
      const w = m.cuffLength, h = m.cuffHeight;
      const path = `M ${-w/2} 0 L ${w/2} 0 L ${w/2} ${h} L ${-w/2} ${h} Z`;
      const polyline = [[-w/2, 0], [w/2, 0], [w/2, h], [-w/2, h], [-w/2, 0]];
      const dims = [
        { kind: 'h', y: -4,      x1: -w/2, x2: w/2, label: `Cuff ${w}` },
        { kind: 'v', x: w/2 + 4, y1: 0, y2: h, label: `H ${h}` },
      ];
      return { path, polyline, dims, width: w + 20, height: h + 20 };
    },
  },

  // -------------------------- COLLAR BAND ----------------------------------
  collar_band: {
    name: 'Collar Band',
    icon: '👔',
    measurements: {
      neckLength:  { label: 'Length (around neck)', default: 42, unit: 'cm', group: 'Width',  min: 20, max: 70 },
      bandHeight:  { label: 'Band Height',          default: 3,  unit: 'cm', group: 'Length', min: 1, max: 10 },
      pointDrop:   { label: 'Center Front Drop',    default: 1,  unit: 'cm', group: 'Length', min: 0, max: 5 },
    },
    build: (m) => {
      const w = m.neckLength, h = m.bandHeight, drop = m.pointDrop;
      // Slight curve on the lower edge for neck fit
      const path = [
        M_(-w/2, 0), L_(w/2, 0), L_(w/2, h),
        Q_(0, h + drop, -w/2, h), 'Z'
      ].join(' ');
      const polyline = [
        [-w/2, 0], [w/2, 0], [w/2, h],
        ...sampleQuad(w/2, h, 0, h + drop, -w/2, h),
        [-w/2, 0],
      ];
      const dims = [
        { kind: 'h', y: -4,      x1: -w/2, x2: w/2, label: `Neck ${w}` },
        { kind: 'v', x: w/2 + 4, y1: 0, y2: h + drop, label: `H ${(h+drop).toFixed(1)}` },
      ];
      return { path, polyline, dims, width: w + 20, height: h + drop + 20 };
    },
  },

  // -------------------------- YOKE (back yoke) -----------------------------
  yoke: {
    name: 'Yoke (Back)',
    icon: '🧥',
    measurements: {
      yokeWidth:   { label: 'Shoulder-to-Shoulder', default: 44, unit: 'cm', group: 'Width',  min: 20, max: 80 },
      yokeDepth:   { label: 'Center Back Depth',    default: 10, unit: 'cm', group: 'Length', min: 3, max: 25 },
      neckWidth:   { label: 'Neck Opening Width',   default: 18, unit: 'cm', group: 'Neck',   min: 6, max: 35 },
      neckDrop:    { label: 'Neck Drop',            default: 2,  unit: 'cm', group: 'Neck',   min: 0.5, max: 8 },
      shoulderSlope: { label: 'Shoulder Slope',     default: 3,  unit: 'cm', group: 'Shoulder',min: 0, max: 10 },
    },
    build: (m) => {
      const wH = m.yokeWidth/2, d = m.yokeDepth, nH = m.neckWidth/2, nd = m.neckDrop, slope = m.shoulderSlope;
      const path = [
        M_(-wH, slope), L_(-nH, d),
        Q_(-nH*0.55, d - nd - 0.3, 0, d - nd),
        Q_(nH*0.55, d - nd - 0.3, nH, d),
        L_(wH, slope), L_(wH, 0), L_(-wH, 0), 'Z'
      ].join(' ');
      const polyline = [
        [-wH, slope], [-nH, d],
        ...sampleQuad(-nH, d, -nH*0.55, d - nd - 0.3, 0, d - nd),
        ...sampleQuad(0, d - nd, nH*0.55, d - nd - 0.3, nH, d),
        [wH, slope], [wH, 0], [-wH, 0], [-wH, slope],
      ];
      const dims = [
        { kind: 'h', y: -4, x1: -wH, x2: wH, label: `W ${m.yokeWidth}` },
        { kind: 'v', x: wH + 4, y1: 0, y2: d, label: `D ${d}` },
      ];
      return { path, polyline, dims, width: wH * 2 + 20, height: d + 20 };
    },
  },

  // -------------------------- PATCH POCKET ---------------------------------
  pocket: {
    name: 'Patch Pocket',
    icon: '🎒',
    measurements: {
      pocketWidth:  { label: 'Width',         default: 14, unit: 'cm', group: 'Width',  min: 6,  max: 30 },
      pocketHeight: { label: 'Height',        default: 16, unit: 'cm', group: 'Length', min: 6,  max: 35 },
      hemFold:      { label: 'Top Hem Fold',  default: 2.5,unit: 'cm', group: 'Length', min: 0, max: 6 },
      bottomCorner: { label: 'Bottom Corner Cut', default: 0, unit: 'cm', group: 'Width', min: 0, max: 6 },
    },
    build: (m) => {
      const w = m.pocketWidth, h = m.pocketHeight + m.hemFold, c = m.bottomCorner;
      let path, polyline;
      if (c > 0) {
        // Chamfered bottom corners
        path = [
          M_(-w/2 + c, 0), L_(w/2 - c, 0), L_(w/2, c),
          L_(w/2, h), L_(-w/2, h), L_(-w/2, c), 'Z'
        ].join(' ');
        polyline = [[-w/2 + c, 0], [w/2 - c, 0], [w/2, c], [w/2, h], [-w/2, h], [-w/2, c], [-w/2 + c, 0]];
      } else {
        path = `M ${-w/2} 0 L ${w/2} 0 L ${w/2} ${h} L ${-w/2} ${h} Z`;
        polyline = [[-w/2, 0], [w/2, 0], [w/2, h], [-w/2, h], [-w/2, 0]];
      }
      const dims = [
        { kind: 'h', y: -4, x1: -w/2, x2: w/2, label: `W ${w}` },
        { kind: 'v', x: w/2 + 4, y1: 0, y2: h, label: `H ${h}` },
      ];
      return { path, polyline, dims, width: w + 20, height: h + 20 };
    },
  },

  // -------------------------- A-LINE SKIRT PANEL ---------------------------
  skirt_panel: {
    name: 'A-Line Skirt Panel',
    icon: '👗',
    measurements: {
      waistWidth:  { label: 'Waist (½)',  default: 34, unit: 'cm', group: 'Width',  min: 10, max: 80 },
      hemWidth:    { label: 'Hem (½)',    default: 54, unit: 'cm', group: 'Width',  min: 15, max: 120 },
      skirtLength: { label: 'Length',     default: 55, unit: 'cm', group: 'Length', min: 20, max: 120 },
      sideCurve:   { label: 'Side Curve', default: 2,  unit: 'cm', group: 'Width',  min: 0,  max: 8 },
    },
    build: (m) => {
      const wH = m.waistWidth/2, hmH = m.hemWidth/2, L = m.skirtLength, cv = m.sideCurve;
      const path = [
        M_(-hmH, 0), L_(hmH, 0),
        Q_((hmH + wH)/2 + cv, L/2, wH, L),
        L_(-wH, L),
        Q_(-(hmH + wH)/2 - cv, L/2, -hmH, 0), 'Z'
      ].join(' ');
      const polyline = [
        [-hmH, 0], [hmH, 0],
        ...sampleQuad(hmH, 0, (hmH + wH)/2 + cv, L/2, wH, L),
        [-wH, L],
        ...sampleQuad(-wH, L, -(hmH + wH)/2 - cv, L/2, -hmH, 0),
        [-hmH, 0],
      ];
      const dims = [
        { kind: 'h', y: -4,    x1: -hmH, x2: hmH, label: `Hem ${m.hemWidth}` },
        { kind: 'h', y: L + 4, x1: -wH,  x2: wH,  label: `Waist ${m.waistWidth}` },
        { kind: 'v', x: hmH + 4, y1: 0, y2: L, label: `L ${L}` },
      ];
      return { path, polyline, dims, width: hmH * 2 + 20, height: L + 20 };
    },
  },

  // -------------------------- HOOD HALF ------------------------------------
  hood: {
    name: 'Hood (half)',
    icon: '🧢',
    measurements: {
      hoodHeight:   { label: 'Total Height',  default: 38, unit: 'cm', group: 'Length', min: 15, max: 60 },
      hoodDepth:    { label: 'Face Depth',    default: 28, unit: 'cm', group: 'Width',  min: 15, max: 50 },
      neckWidth:    { label: 'Neck Edge',     default: 22, unit: 'cm', group: 'Neck',   min: 10, max: 40 },
      faceOpening:  { label: 'Face Opening',  default: 30, unit: 'cm', group: 'Width',  min: 15, max: 55 },
      topCurve:     { label: 'Top Curvature', default: 4,  unit: 'cm', group: 'Length', min: 0, max: 12 },
    },
    build: (m) => {
      // Stand-up hood half: neck edge at bottom, back seam on left (x<0),
      // face opening on the right, crown curve at the top.
      const H = m.hoodHeight, D = m.hoodDepth, N = m.neckWidth, F = m.faceOpening, cv = m.topCurve;
      const path = [
        M_(0, 0), L_(N, 0),
        Q_(D * 0.6, H * 0.25, D * 0.9, H - F),
        L_(D * 0.6, H),
        Q_(D * 0.3, H + cv, 0, H),
        'Z'
      ].join(' ');
      const polyline = [
        [0, 0], [N, 0],
        ...sampleQuad(N, 0, D * 0.6, H * 0.25, D * 0.9, H - F),
        [D * 0.6, H],
        ...sampleQuad(D * 0.6, H, D * 0.3, H + cv, 0, H),
        [0, 0],
      ];
      const dims = [
        { kind: 'h', y: -4, x1: 0, x2: N, label: `Neck ${N}` },
        { kind: 'v', x: D + 4, y1: 0, y2: H, label: `H ${H}` },
      ];
      return { path, polyline, dims, width: D + 20, height: H + 20 };
    },
  },
};

// ============================================================================
//  SVG HELPERS — rasterise & build full SVG markup
// ============================================================================
export const svgToPng = (svgMarkup, targetW = 800, targetH = 800) => new Promise((resolve) => {
  const blob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const img  = new Image();
  img.onload = () => {
    const c = document.createElement('canvas');
    c.width = targetW; c.height = targetH;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, targetW, targetH);
    const ar = img.width / img.height;
    let w = targetW, h = targetH;
    if (ar > 1) h = targetW / ar; else w = targetH * ar;
    ctx.drawImage(img, (targetW - w)/2, (targetH - h)/2, w, h);
    URL.revokeObjectURL(url); resolve(c.toDataURL('image/png'));
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(''); };
  img.src = url;
});

export const buildSpecSvgMarkup = (template, measurements, showDims = true, extra = {}) => {
  const tpl = GARMENT_TEMPLATES[template];
  if (!tpl) return '';
  const shape = tpl.build(measurements);
  const PAD = 15;
  const vbW = shape.width, vbH = shape.height;

  const dimsMarkup = (showDims ? (shape.dims || []) : []).map(d => {
    if (d.kind === 'h') {
      const sx1 = d.x1 + vbW/2, sx2 = d.x2 + vbW/2, sy = vbH - d.y;
      const midX = (sx1 + sx2) / 2;
      return `<line x1="${sx1}" y1="${sy}" x2="${sx2}" y2="${sy}" stroke="#3b82f6" stroke-width="0.3" stroke-dasharray="1,0.8"/>` +
             `<text x="${midX}" y="${sy - 1}" font-size="2.2" fill="#1d4ed8" text-anchor="middle" font-family="monospace">${d.label}</text>`;
    } else {
      const sx = d.x + vbW/2, sy1 = vbH - d.y1, sy2 = vbH - d.y2;
      const midY = (sy1 + sy2) / 2;
      return `<line x1="${sx}" y1="${sy1}" x2="${sx}" y2="${sy2}" stroke="#3b82f6" stroke-width="0.3" stroke-dasharray="1,0.8"/>` +
             `<text x="${sx + 0.5}" y="${midY}" font-size="2.2" fill="#1d4ed8" text-anchor="start" font-family="monospace" transform="rotate(-90 ${sx + 0.5} ${midY})">${d.label}</text>`;
    }
  }).join('');

  // Optional overlaid SA path (drawn as dashed outer outline)
  const saExtra = extra.saPath ?
    `<g transform="translate(${vbW/2}, ${vbH}) scale(1, -1)">
       <path d="${extra.saPath}" fill="none" stroke="#dc2626" stroke-width="0.4" stroke-dasharray="2,1"/>
     </g>` : '';

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${-PAD} ${-PAD} ${vbW + PAD*2} ${vbH + PAD*2}">
    <rect x="${-PAD}" y="${-PAD}" width="${vbW + PAD*2}" height="${vbH + PAD*2}" fill="#ffffff"/>
    <g transform="translate(${vbW/2}, ${vbH}) scale(1, -1)">
      <path d="${shape.path}" fill="rgba(59,130,246,0.06)" stroke="#1e293b" stroke-width="0.6" stroke-linejoin="round"/>
    </g>
    ${saExtra}
    ${dimsMarkup}
  </svg>`;
};

// ============================================================================
//  SPEC-SHEET IMPORTER
// ============================================================================
export const parseSpecSheet = (raw) => {
  if (!raw) return {};
  const txt = raw.trim();
  if (txt.startsWith('{') || txt.startsWith('[')) {
    try {
      const j = JSON.parse(txt);
      if (Array.isArray(j)) {
        const out = {};
        j.forEach(row => {
          const k = row.key || row.measurement || row.name;
          const v = row.value ?? row.val ?? row.current ?? row.target;
          if (k != null) out[String(k).trim()] = parseFloat(v);
        });
        return out;
      }
      const out = {};
      Object.entries(j).forEach(([k, v]) => { out[k.trim()] = parseFloat(v); });
      return out;
    } catch (_) { /* fallthrough */ }
  }
  const out = {};
  txt.split(/\r?\n/).forEach(line => {
    const parts = line.split(/[,\t:;=|]/).map(s => s.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const k = parts[0];
      const v = parseFloat(parts[parts.length - 1]);
      if (!isNaN(v)) out[k] = v;
    }
  });
  return out;
};

export const fuzzyMatchSpec = (raw, templateKey) => {
  const tpl = GARMENT_TEMPLATES[templateKey];
  if (!tpl) return {};
  const rawKeys = Object.keys(raw);
  const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');
  const result = {};
  Object.entries(tpl.measurements).forEach(([key, def]) => {
    const targets = [norm(key), norm(def.label)];
    const hit = rawKeys.find(rk => {
      const nk = norm(rk);
      return targets.some(t => t && (nk === t || nk.includes(t) || t.includes(nk)));
    });
    if (hit && !isNaN(raw[hit])) result[key] = raw[hit];
  });
  return result;
};

// ============================================================================
//  SEAM ALLOWANCE ENGINE
// ----------------------------------------------------------------------------
//  Real-world: spec-sheet measurements are the FINISHED garment. The CUT
//  pattern is the finished panel + seam allowance around every sewn edge.
//  We offset the closed polyline outward by `sa` (in cm). Corners use a
//  bisector + miter-limit to stay sane on sharp angles.
// ============================================================================
const _norm = ([x, y]) => { const l = Math.hypot(x, y) || 1; return [x / l, y / l]; };

export function offsetPolygon(points, offset) {
  // Assumes points are CCW (y-up world). Positive offset = outward.
  // De-dup last point if it equals first (closed loop convention)
  const pts = (points[0][0] === points[points.length - 1][0] &&
               points[0][1] === points[points.length - 1][1])
    ? points.slice(0, -1) : points.slice();

  // Ensure CCW orientation (signed area > 0 for CCW with y-up)
  let signedArea = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x1, y1] = pts[i], [x2, y2] = pts[(i + 1) % pts.length];
    signedArea += (x2 - x1) * (y2 + y1);
  }
  // With y-up, CCW has NEGATIVE shoelace; fix sign convention here
  const isCCW = signedArea < 0;
  const work = isCCW ? pts : pts.slice().reverse();

  const n = work.length;
  const out = [];
  for (let i = 0; i < n; i++) {
    const prev = work[(i - 1 + n) % n];
    const curr = work[i];
    const next = work[(i + 1) % n];
    const e1 = [curr[0] - prev[0], curr[1] - prev[1]];
    const e2 = [next[0] - curr[0], next[1] - curr[1]];
    // Outward normal (rotate edge 90° CW for CCW polygon with y-up)
    const n1 = _norm([e1[1], -e1[0]]);
    const n2 = _norm([e2[1], -e2[0]]);
    const avgN = _norm([n1[0] + n2[0], n1[1] + n2[1]]);
    const cosHalf = avgN[0] * n1[0] + avgN[1] * n1[1]; // dot of bisector and one normal
    const miter = offset / Math.max(0.25, cosHalf);    // clamp to avoid explosion
    out.push([curr[0] + avgN[0] * miter, curr[1] + avgN[1] * miter]);
  }
  if (!isCCW) out.reverse();
  out.push(out[0]); // close
  return out;
}

// Build an SVG path string from a polyline
export const polylineToPath = (pts) => {
  if (!pts || pts.length === 0) return '';
  let d = `M ${pts[0][0].toFixed(2)} ${pts[0][1].toFixed(2)}`;
  for (let i = 1; i < pts.length; i++) d += ` L ${pts[i][0].toFixed(2)} ${pts[i][1].toFixed(2)}`;
  return d + ' Z';
};

// Polygon area (absolute, assumes closed polyline)
export const polygonArea = (pts) => {
  const p = (pts[0][0] === pts[pts.length - 1][0] && pts[0][1] === pts[pts.length - 1][1])
    ? pts.slice(0, -1) : pts;
  let s = 0;
  for (let i = 0; i < p.length; i++) {
    const [x1, y1] = p[i], [x2, y2] = p[(i + 1) % p.length];
    s += x1 * y2 - x2 * y1;
  }
  return Math.abs(s) / 2;
};

// Axis-aligned bounding box of polyline
export const polylineBBox = (pts) => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [x, y] of pts) {
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
};

// ============================================================================
//  POLYGON GEOMETRY HELPERS (for real-polygon marker making)
// ----------------------------------------------------------------------------
//  rotatePolyline  : rotate polyline around origin by angle (degrees)
//  shiftPolyline   : shift so bbox starts at (dx, dy)
//  normalizePolylineToOrigin : rotate, then shift so bbox starts at (0, 0)
//  segmentsIntersect, pointInPolygon, polygonsOverlap
// ============================================================================
export const rotatePolyline = (pts, angleDeg) => {
  if (!angleDeg) return pts.map(p => [p[0], p[1]]);
  const rad = angleDeg * Math.PI / 180;
  const cos = Math.cos(rad), sin = Math.sin(rad);
  return pts.map(([x, y]) => [x * cos - y * sin, x * sin + y * cos]);
};

export const shiftPolyline = (pts, dx, dy) => pts.map(([x, y]) => [x + dx, y + dy]);

// Rotate + shift so bbox lives in [0, w] × [0, h]
export const normalizePolyline = (pts, angleDeg = 0) => {
  const rotated = rotatePolyline(pts, angleDeg);
  const bb = polylineBBox(rotated);
  return {
    poly: shiftPolyline(rotated, -bb.minX, -bb.minY),
    width: bb.width,
    height: bb.height,
  };
};

// Translate polyline to an absolute position on the marker
export const placePolyline = (normalizedPoly, x, y) => shiftPolyline(normalizedPoly, x, y);

// Segment-segment intersection (strict, rejects touching endpoints as non-crossing)
const segmentsIntersect = (a, b, c, d) => {
  const d1x = b[0] - a[0], d1y = b[1] - a[1];
  const d2x = d[0] - c[0], d2y = d[1] - c[1];
  const denom = d1x * d2y - d1y * d2x;
  if (Math.abs(denom) < 1e-10) return false; // parallel
  const t = ((c[0] - a[0]) * d2y - (c[1] - a[1]) * d2x) / denom;
  const u = ((c[0] - a[0]) * d1y - (c[1] - a[1]) * d1x) / denom;
  return t > 1e-6 && t < 1 - 1e-6 && u > 1e-6 && u < 1 - 1e-6;
};

// Point-in-polygon (ray casting)
export const pointInPolygon = (pt, poly) => {
  const [x, y] = pt;
  let inside = false;
  const n = poly.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / ((yj - yi) || 1e-12) + xi)) {
      inside = !inside;
    }
  }
  return inside;
};

// Polygon-polygon overlap (bbox reject, then edge test, then containment test)
export const polygonsOverlap = (p1, p2, gap = 0) => {
  const bb1 = polylineBBox(p1), bb2 = polylineBBox(p2);
  if (bb1.maxX + gap < bb2.minX || bb2.maxX + gap < bb1.minX) return false;
  if (bb1.maxY + gap < bb2.minY || bb2.maxY + gap < bb1.minY) return false;
  // Drop duplicate closing vertex if present
  const norm = (p) => (p[0][0] === p[p.length - 1][0] && p[0][1] === p[p.length - 1][1]) ? p.slice(0, -1) : p;
  const a = norm(p1), b = norm(p2);
  // Edge-edge intersection
  for (let i = 0; i < a.length; i++) {
    const a1 = a[i], a2 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j++) {
      const b1 = b[j], b2 = b[(j + 1) % b.length];
      if (segmentsIntersect(a1, a2, b1, b2)) return true;
    }
  }
  // One fully inside the other
  if (pointInPolygon(a[0], b)) return true;
  if (pointInPolygon(b[0], a)) return true;
  // If gap>0, check minimum distance between edges (approximate via vertex-to-segment)
  if (gap > 0) {
    const distPointToSeg = (p, s1, s2) => {
      const vx = s2[0] - s1[0], vy = s2[1] - s1[1];
      const wx = p[0] - s1[0], wy = p[1] - s1[1];
      const c1 = vx * wx + vy * wy;
      if (c1 <= 0) return Math.hypot(p[0] - s1[0], p[1] - s1[1]);
      const c2 = vx * vx + vy * vy;
      if (c2 <= c1) return Math.hypot(p[0] - s2[0], p[1] - s2[1]);
      const t = c1 / c2;
      return Math.hypot(p[0] - s1[0] - t * vx, p[1] - s1[1] - t * vy);
    };
    for (const pt of a) for (let j = 0; j < b.length; j++) {
      if (distPointToSeg(pt, b[j], b[(j + 1) % b.length]) < gap) return true;
    }
    for (const pt of b) for (let j = 0; j < a.length; j++) {
      if (distPointToSeg(pt, a[j], a[(j + 1) % a.length]) < gap) return true;
    }
  }
  return false;
};

// ============================================================================
//  2D POLYGON-AWARE PACKING (upgraded marker maker)
// ----------------------------------------------------------------------------
//  Each part carries a true polyline outline. We try 4 rotations (0/90/180/
//  270), rasterise a coarse grid to find bottom-left-fill positions, and use
//  actual polygon overlap for collision. Result is tighter than bbox packing
//  and — crucially — every placement stores the rotated polyline so the
//  marker renders the real shape, not a rectangle.
//
//  This is NOT full No-Fit-Polygon nesting (that's another day of code), but
//  it handles concave/convex shapes correctly and supports user drag-to-
//  improve via the SpreadLayoutTool UI.
// ============================================================================
export function packPartsIntoSpread(parts, fabricLength, fabricWidth, opts = {}) {
  const {
    allowRotate = true,
    margin = 0.5,           // SA/buffer gap in cm between parts
    rotations = [0, 90, 180, 270], // allowed rotation angles
    gridStep = 2,           // cm — scan granularity for placement search
    iterations = 1,         // how many shuffles to try; best yield wins
    sortMode = 'area_desc', // 'area_desc' | 'height_desc' | 'random'
  } = opts;

  // Pre-compute a normalized polyline + bbox for every (part, rotation) combo
  const prep = parts.map(p => {
    const angles = allowRotate ? rotations : [0];
    const orients = angles.map(ang => {
      const norm = normalizePolyline(p.outline || p.polyline, ang);
      return { angle: ang, poly: norm.poly, w: norm.width, h: norm.height };
    });
    return {
      id: p.id, name: p.name, size: p.size, template: p.template,
      measurements: p.measurements, polyArea: p.polyArea,
      orients,
    };
  });

  // Single-pass greedy placement using polygon overlap
  const singlePass = (order) => {
    const placed = [];
    for (const it of order) {
      // Try orientations in order of area efficiency (tallest fits first improves strips)
      let bestPlace = null;
      for (const o of it.orients) {
        if (o.h + margin * 2 > fabricWidth) continue;
        if (o.w + margin * 2 > fabricLength) continue;
        // Bottom-left-fill scan
        for (let y = 0; y + o.h + margin <= fabricWidth; y += gridStep) {
          for (let x = 0; x + o.w + margin <= fabricLength; x += gridStep) {
            // Shift candidate polygon to (x+margin, y+margin)
            const cand = placePolyline(o.poly, x + margin, y + margin);
            let collides = false;
            for (const pl of placed) {
              if (polygonsOverlap(cand, pl.worldPoly, margin)) { collides = true; break; }
            }
            if (!collides) {
              bestPlace = {
                partId: it.id, name: it.name, size: it.size,
                x: x + margin, y: y + margin,
                bboxW: o.w, bboxH: o.h,
                rotation: o.angle,
                localPoly: o.poly,                 // 0-origin shape after rotation
                worldPoly: cand,                   // absolute coords on fabric
              };
              break;
            }
          }
          if (bestPlace) break;
        }
        if (bestPlace) break;
      }
      if (bestPlace) placed.push(bestPlace);
    }
    return placed;
  };

  // Sort variants
  const sortItems = (mode) => {
    const copy = prep.slice();
    if (mode === 'height_desc') copy.sort((a, b) => b.orients[0].h - a.orients[0].h);
    else if (mode === 'random') copy.sort(() => Math.random() - 0.5);
    else /* area_desc */ copy.sort((a, b) => (b.polyArea || 0) - (a.polyArea || 0));
    return copy;
  };

  // Multi-pass: primary sort + extra random shuffles, keep the best yield
  let best = singlePass(sortItems(sortMode));
  const extras = Math.max(0, iterations - 1);
  for (let i = 0; i < extras; i++) {
    const trial = singlePass(sortItems('random'));
    const bestUsed = best.reduce((m, p) => Math.max(m, p.x + p.bboxW), 0);
    const trialUsed = trial.reduce((m, p) => Math.max(m, p.x + p.bboxW), 0);
    const bestScore = best.length - (bestUsed / fabricLength) * 0.1; // placed count > compact
    const trialScore = trial.length - (trialUsed / fabricLength) * 0.1;
    if (trialScore > bestScore) best = trial;
  }

  const placements = best;
  const usedArea   = parts.reduce((s, p) => s + (p.polyArea || 0), 0);
  const fabricArea = fabricLength * fabricWidth;
  const yieldPct   = fabricArea > 0 ? (usedArea / fabricArea) * 100 : 0;
  const usedLength = placements.reduce((m, p) => Math.max(m, p.x + p.bboxW), 0);
  const placedCount  = placements.length;
  const totalCount   = parts.length;

  return { placements, fabricLength, fabricWidth, usedLength, usedArea, fabricArea, yieldPct, placedCount, totalCount };
}

// ============================================================================
//  ShapePreview — live SVG renderer (used by SpecDrivenShape, Assembly, etc.)
// ============================================================================

// ============================================================================
//  ShapeEditor — interactive node-drag version of ShapePreview
//  Shows the parametric shape with draggable handles on every polyline vertex.
//  Drag a node to store an override delta; the shape follows immediately.
//  The overridden polyline is exported as tracedPoints for the NPF.
// ============================================================================
export function ShapeEditor({ template, measurements, showDims, zoom, saPath,
                              nodeOverrides, onNodeOverride, strokeColor = '#1e293b' }) {
  const tpl = GARMENT_TEMPLATES[template];
  if (!tpl) return <div style={{padding:20,color:'#ef4444'}}>Unknown template</div>;
  let baseShape;
  try { baseShape = tpl.build(measurements); } catch(e) { return <div style={{padding:20,color:'#ef4444',fontSize:'0.8rem'}}>Shape error: {e.message}</div>; }

  const PAD = 15, vbW = baseShape.width, vbH = baseShape.height;
  const sc  = Math.min(480/vbW, 480/vbH) * (zoom||1);
  const svgRef = useRef(null);

  // Apply overrides to polyline
  const polyline = baseShape.polyline.map((pt, i) => {
    const ov = nodeOverrides?.[i];
    return ov ? [pt[0]+ov.dx, pt[1]+ov.dy] : [pt[0], pt[1]];
  });

  // Build an overridden SVG path from the modified polyline
  const pathFromPoly = (pts) => {
    if (!pts.length) return '';
    let d = `M ${pts[0][0].toFixed(2)} ${pts[0][1].toFixed(2)}`;
    for (let i = 1; i < pts.length; i++) d += ` L ${pts[i][0].toFixed(2)} ${pts[i][1].toFixed(2)}`;
    return d + ' Z';
  };
  const overriddenPath = (nodeOverrides && Object.keys(nodeOverrides).length)
    ? pathFromPoly(polyline) : null;

  // toScreen / fromScreen in viewBox space
  const toScreen  = (x, y) => [x + vbW/2, vbH - y];
  const fromScreen = (sx, sy) => [sx - vbW/2, vbH - sy];

  // Get SVG-space point from a mouse event
  const getSvgPt = (e) => {
    const svg = svgRef.current; if (!svg) return [0,0];
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const ctm = svg.getScreenCTM(); if (!ctm) return [0,0];
    const sp = pt.matrixTransform(ctm.inverse());
    return [sp.x, sp.y];
  };

  const dragging  = useRef(null);  // { idx, startSvgX, startSvgY, origOv }

  const onNodeDown = (e, idx) => {
    e.stopPropagation(); e.preventDefault();
    const [sx,sy] = getSvgPt(e);
    dragging.current = { idx, sx, sy, ox: nodeOverrides?.[idx]?.dx||0, oy: nodeOverrides?.[idx]?.dy||0 };
    window.addEventListener('mousemove', onWindowMove);
    window.addEventListener('mouseup',   onWindowUp);
  };

  const onWindowMove = (e) => {
    if (!dragging.current) return;
    const { idx, sx, sy, ox, oy } = dragging.current;
    const [cx,cy] = getSvgPt(e);
    // Delta in SVG viewBox space → convert to pattern-cm space
    // toScreen maps cm→svgPx via (x+vbW/2, vbH-y); inverse: dx_cm = dsvgX, dy_cm = -dsvgY
    const dx = cx - sx, dy = cy - sy;
    onNodeOverride && onNodeOverride(idx, { dx: ox + dx, dy: oy - dy });
  };

  const onWindowUp = () => {
    dragging.current = null;
    window.removeEventListener('mousemove', onWindowMove);
    window.removeEventListener('mouseup',   onWindowUp);
  };

  // Compute node screen positions
  const nodePositions = polyline.map(([x,y]) => toScreen(x,y));

  const dimEls = (showDims ? (baseShape.dims||[]) : []).map((d,i) => {
    if (d.kind==='h') {
      const [sx1,sy] = toScreen(d.x1,d.y), [sx2] = toScreen(d.x2,d.y);
      return <g key={i}>
        <line x1={sx1} y1={sy} x2={sx2} y2={sy} stroke="#3b82f6" strokeWidth={0.3} strokeDasharray="1,0.8"/>
        <text x={(sx1+sx2)/2} y={sy-1} fontSize="2.2" fill="#1d4ed8" textAnchor="middle">{d.label}</text>
      </g>;
    } else {
      const [sx,sy1] = toScreen(d.x,d.y1), [,sy2] = toScreen(d.x,d.y2);
      const mY = (sy1+sy2)/2;
      return <g key={i}>
        <line x1={sx} y1={sy1} x2={sx} y2={sy2} stroke="#3b82f6" strokeWidth={0.3} strokeDasharray="1,0.8"/>
        <text x={sx+0.5} y={mY} fontSize="2.2" fill="#1d4ed8" textAnchor="start" transform={`rotate(-90 ${sx+0.5} ${mY})`}>{d.label}</text>
      </g>;
    }
  });

  return (
    <svg ref={svgRef}
      viewBox={`${-PAD} ${-PAD} ${vbW+PAD*2} ${vbH+PAD*2}`}
      width={baseShape.width*sc+PAD*2} height={baseShape.height*sc+PAD*2}
      style={{ background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:6, cursor:'crosshair' }}>
      <g transform={`translate(${vbW/2}, ${vbH}) scale(1,-1)`}>
        {/* Original shape (faint guide) */}
        {overriddenPath && <path d={baseShape.path} fill="none" stroke="#cbd5e1" strokeWidth={0.4} strokeDasharray="2,1"/>}
        {/* Overridden / current shape */}
        <path d={overriddenPath || baseShape.path}
          fill="rgba(59,130,246,0.06)" stroke={strokeColor} strokeWidth={0.6} strokeLinejoin="round"/>
        {saPath && <path d={saPath} fill="none" stroke="#dc2626" strokeWidth={0.4} strokeDasharray="2,1"/>}
        {/* Polyline connection lines between nodes */}
        {polyline.map((_,i) => {
          if (i===0) return null;
          const [x1,y1]=polyline[i-1], [x2,y2]=polyline[i];
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#6366f1" strokeWidth={0.2} strokeDasharray="1,1" opacity="0.5"/>;
        })}
      </g>
      <g>{dimEls}</g>
      {/* Draggable node handles (in screen space, outside the y-flip transform) */}
      {nodePositions.map(([sx,sy], i) => {
        const isOverridden = !!(nodeOverrides?.[i]?.dx || nodeOverrides?.[i]?.dy);
        return (
          <circle key={i} cx={sx} cy={sy}
            r={isOverridden ? 1.8 : 1.4}
            fill={isOverridden ? '#f59e0b' : '#6366f1'}
            stroke="white" strokeWidth={0.5}
            onMouseDown={e => onNodeDown(e, i)}
            style={{ cursor:'grab' }}
          />
        );
      })}
    </svg>
  );
}

export function ShapePreview({ template, measurements, showDims, zoom, strokeColor = '#1e293b', saPath = null }) {
  const tpl = GARMENT_TEMPLATES[template];
  if (!tpl) return <div style={{ padding: 20, color: '#ef4444' }}>Unknown template</div>;
  let shape;
  try { shape = tpl.build(measurements); }
  catch (e) { return <div style={{ padding: 20, color: '#ef4444', fontSize: '0.8rem' }}>Shape error: {e.message}</div>; }

  const PAD = 15, vbW = shape.width, vbH = shape.height;
  const basePx = 480;
  const scale = Math.min(basePx / vbW, basePx / vbH) * (zoom || 1);
  const renderW = vbW * scale, renderH = vbH * scale;
  const toScreen = (x, y) => [x + vbW/2, vbH - y];

  const dimEls = (showDims ? (shape.dims || []) : []).map((d, i) => {
    if (d.kind === 'h') {
      const [sx1, sy] = toScreen(d.x1, d.y);
      const [sx2]     = toScreen(d.x2, d.y);
      const midX = (sx1 + sx2) / 2;
      return (
        <g key={i}>
          <line x1={sx1} y1={sy} x2={sx2} y2={sy} stroke="#3b82f6" strokeWidth={0.3} strokeDasharray="1,0.8" />
          <text x={midX} y={sy - 1} fontSize="2.2" fill="#1d4ed8" textAnchor="middle" style={{ fontFamily: 'monospace' }}>{d.label}</text>
        </g>
      );
    } else {
      const [sx, sy1] = toScreen(d.x, d.y1);
      const [, sy2]   = toScreen(d.x, d.y2);
      const midY = (sy1 + sy2) / 2;
      return (
        <g key={i}>
          <line x1={sx} y1={sy1} x2={sx} y2={sy2} stroke="#3b82f6" strokeWidth={0.3} strokeDasharray="1,0.8" />
          <text x={sx + 0.5} y={midY} fontSize="2.2" fill="#1d4ed8" textAnchor="start" style={{ fontFamily: 'monospace' }} transform={`rotate(-90 ${sx + 0.5} ${midY})`}>{d.label}</text>
        </g>
      );
    }
  });

  return (
    <svg
      viewBox={`${-PAD} ${-PAD} ${vbW + PAD*2} ${vbH + PAD*2}`}
      width={renderW + PAD * 2}
      height={renderH + PAD * 2}
      xmlns="http://www.w3.org/2000/svg"
      style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 6 }}
    >
      <g transform={`translate(${vbW/2}, ${vbH}) scale(1, -1)`}>
        <path d={shape.path} fill="rgba(59,130,246,0.06)" stroke={strokeColor} strokeWidth={0.6} strokeLinejoin="round" />
        {saPath && <path d={saPath} fill="none" stroke="#dc2626" strokeWidth={0.4} strokeDasharray="2,1" />}
      </g>
      <g>{dimEls}</g>
    </svg>
  );
}

// ============================================================================
//  SpecDrivenShape — the inline tool PartDNA embeds in its "Spec Builder" tab
// ----------------------------------------------------------------------------
//  Exported so PartDNA.js can import it without duplicating code.
//  Adds: seam-allowance toggle + cm input, "1:1 print" button, fullscreen
//  is handled by the parent wrapper (PartDNA adds a maximise button).
// ============================================================================
export function SpecDrivenShape({ onSave, initialSpecs, initialTemplate, onSpecsChange }) {
  const [template, setTemplate]       = useState(initialTemplate || 'tee_front');
  const [measurements, setMeasurements] = useState({});
  const [baseline, setBaseline]       = useState({});
  const [showDims, setShowDims]       = useState(true);
  const [zoom, setZoom]               = useState(1);
  const [importText, setImportText]   = useState('');
  const [importOpen, setImportOpen]   = useState(false);
  const [saveBadge, setSaveBadge]     = useState(false);
  const [aiBusy, setAiBusy]           = useState(false);
  const [aiMsg, setAiMsg]             = useState('');
  const [saEnabled, setSaEnabled]     = useState(false);
  const [saCm, setSaCm]               = useState(1.0);
  const [nodeEditMode, setNodeEditMode] = useState(false);
  const [nodeOverrides, setNodeOverrides] = useState({});  // {[idx]: {dx,dy}} in cm

  // Initialise measurements whenever template changes
  useEffect(() => {
    const t = GARMENT_TEMPLATES[template];
    if (!t) return;
    const def = {};
    Object.entries(t.measurements).forEach(([k, d]) => { def[k] = d.default; });
    const merged = { ...def, ...(initialSpecs?.measurements || {}) };
    setMeasurements(merged);
    setBaseline({ ...def, ...(initialSpecs?.baseline || initialSpecs?.measurements || {}) });
    setNodeOverrides({});  // clear overrides when template changes
    // eslint-disable-next-line
  }, [template]);

  useEffect(() => {
    if (onSpecsChange && Object.keys(measurements).length) {
      onSpecsChange({ template, measurements: { ...measurements }, baseline: { ...baseline }, seamAllowanceCm: saEnabled ? saCm : 0 });
    }
    // eslint-disable-next-line
  }, [template, measurements, baseline, saEnabled, saCm]);

  const tpl = GARMENT_TEMPLATES[template];
  const groupedMeas = useMemo(() => {
    const g = {};
    Object.entries(tpl.measurements).forEach(([k, def]) => {
      const grp = def.group || 'Other';
      if (!g[grp]) g[grp] = [];
      g[grp].push([k, def]);
    });
    return g;
  }, [tpl]);

  // Compute SA path when enabled (derived state)
  const saPath = useMemo(() => {
    if (!saEnabled || !saCm || Object.keys(measurements).length === 0) return null;
    try {
      const shape = tpl.build(measurements);
      const off = offsetPolygon(shape.polyline, saCm);
      return polylineToPath(off);
    } catch (e) { return null; }
  }, [saEnabled, saCm, measurements, tpl]);

  const commitShape = async () => {
    const svg = buildSpecSvgMarkup(template, measurements, showDims, { saPath });
    const png = await svgToPng(svg, 800, 800);
    if (png) {
      // Also export overridden polyline as tracedPoints (in cm, y-up, centred)
      const baseShape = tpl.build(measurements);
      const modPoly = baseShape.polyline.map((pt, i) => {
        const ov = nodeOverrides[i];
        return ov ? { x: +(pt[0]+ov.dx).toFixed(3), y: +(pt[1]+ov.dy).toFixed(3) }
                  : { x: +pt[0].toFixed(3),           y: +pt[1].toFixed(3) };
      });
      onSave(png, modPoly);  // second arg = tracedPoints for NPF
      setSaveBadge(true);
      setTimeout(() => setSaveBadge(false), 2500);
    }
  };

  const rebaseline       = () => setBaseline({ ...measurements });
  const resetToBaseline  = () => setMeasurements({ ...baseline });

  const handleImportApply = () => {
    const raw = parseSpecSheet(importText);
    const matched = fuzzyMatchSpec(raw, template);
    if (Object.keys(matched).length === 0) {
      alert('No recognised measurement keys found. Expected keys like:\n' +
        Object.entries(tpl.measurements).map(([k, d]) => `  • ${k}  (${d.label})`).join('\n'));
      return;
    }
    setMeasurements(prev => ({ ...prev, ...matched }));
    setImportText(''); setImportOpen(false);
  };

  const handleImportFile = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    const text = await f.text();
    setImportText(text); setImportOpen(true);
  };

  const exportCsv = () => {
    const rows = [['key', 'label', 'original', 'current', 'delta', 'unit']];
    Object.entries(tpl.measurements).forEach(([k, d]) => {
      const o = baseline[k] ?? d.default;
      const c = measurements[k] ?? d.default;
      rows.push([k, d.label, o, c, (c - o).toFixed(2), d.unit]);
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `specs_${template}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // 1:1 actual-size print using browser print CSS
  const printActualSize = () => {
    const shape = tpl.build(measurements);
    const offPoly = saEnabled && saCm ? offsetPolygon(shape.polyline, saCm) : null;
    const saPathD = offPoly ? polylineToPath(offPoly) : '';
    const vbW = shape.width, vbH = shape.height, PAD = 1;
    const html = `<!DOCTYPE html><html><head><title>Print ${tpl.name}</title>
      <style>
        @page { size: auto; margin: 10mm; }
        body { margin:0; font-family: system-ui, sans-serif; }
        .sheet { page-break-after: always; }
        h3 { margin: 0 0 4mm 0; font-size: 12pt; }
        .meta { font-size: 9pt; color: #555; margin-bottom: 4mm; }
        /* 1cm = 10mm = ~37.795 CSS px at 96dpi, but we use mm directly via SVG unit-less scaled by transform */
        svg { display:block; }
        .legend { margin-top:6mm; font-size:9pt; }
        .legend span.net { display:inline-block; width:12px; height:2px; background:#1e293b; vertical-align:middle; margin-right:4px; }
        .legend span.sa  { display:inline-block; width:12px; height:2px; background:#dc2626; vertical-align:middle; margin-right:4px; }
      </style></head><body>
      <div class="sheet">
        <h3>${tpl.name} — 1:1 scale</h3>
        <div class="meta">
          Template: ${template} · Seam allowance: ${saEnabled ? saCm + ' cm' : 'none'} ·
          Printed: ${new Date().toLocaleString()}
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" width="${vbW}mm" height="${vbH}mm"
             viewBox="${-PAD} ${-PAD} ${vbW + PAD*2} ${vbH + PAD*2}">
          <g transform="translate(${vbW/2}, ${vbH}) scale(1,-1)">
            <path d="${shape.path}" fill="none" stroke="#1e293b" stroke-width="0.3"/>
            ${saPathD ? `<path d="${saPathD}" fill="none" stroke="#dc2626" stroke-width="0.3" stroke-dasharray="2,1"/>` : ''}
          </g>
        </svg>
        <div class="legend">
          <span class="net"></span> Net (spec sheet)
          ${saPathD ? '&nbsp;&nbsp;&nbsp;<span class="sa"></span> Cut line (with ' + saCm + 'cm seam allowance)' : ''}
        </div>
      </div>
      <script>window.onload=()=>{setTimeout(()=>window.print(),300);}</script>
      </body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  const runAiShape = async () => {
    setAiBusy(true); setAiMsg('');
    try {
      const payload = {
        template, baseline, target: measurements,
        template_info: {
          name: tpl.name,
          measurements: Object.fromEntries(
            Object.entries(tpl.measurements).map(([k, d]) => [k, { label: d.label, unit: d.unit, group: d.group }])
          )
        },
        referenceImage: initialSpecs?.referenceImage || '',
        instruction: 'Given a garment panel whose shape is defined by the baseline measurements, produce a new shape image that matches the target measurements. Preserve curvature and design intent.'
      };
      const res = await fetch(API.aiShapeInterpret, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      const txt = await res.text();
      let json = {};
      try { json = JSON.parse(txt); } catch (_) { json = { raw: txt }; }
      if (json.image) { onSave(json.image); setAiMsg('✓ AI shape applied'); }
      else if (json.svg) { const png = await svgToPng(json.svg, 800, 800); onSave(png); setAiMsg('✓ AI shape applied'); }
      else { setAiMsg('AI endpoint unavailable – saved parametric shape instead.'); await commitShape(); }
    } catch (e) {
      setAiMsg('AI call failed – saved parametric shape.'); await commitShape();
    } finally {
      setAiBusy(false); setTimeout(() => setAiMsg(''), 6000);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 10, height: '100%', background: 'white' }}>
      {/* LEFT: template + measurements */}
      <div style={{ borderRight: '1px solid #e2e8f0', overflowY: 'auto', padding: '10px 12px' }}>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 10, color: '#64748b', fontWeight: 'bold', letterSpacing: '0.05em' }}>GARMENT TEMPLATE</label>
          <select value={template} onChange={e => setTemplate(e.target.value)}
            style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: 5, fontSize: 12 }}>
            {Object.entries(GARMENT_TEMPLATES).map(([k, t]) => (
              <option key={k} value={k}>{t.icon} {t.name}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: 5, marginBottom: 10 }}>
          <button onClick={() => setImportOpen(v => !v)}
            style={{ flex: 1, padding: '6px', background: '#eef2ff', color: '#4338ca', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 'bold' }}>📥 Import Spec Sheet</button>
          <button onClick={exportCsv} style={{ padding: '6px 10px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 11 }}>CSV</button>
          <button onClick={printActualSize} title="Print 1:1 actual-size pattern"
            style={{ padding: '6px 10px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 11 }}>🖨️</button>
        </div>
        {importOpen && (
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 8, marginBottom: 10 }}>
            <div style={{ fontSize: 10, color: '#64748b', marginBottom: 4 }}>Paste CSV / JSON / key:value lines</div>
            <textarea rows={4} value={importText} onChange={e => setImportText(e.target.value)}
              placeholder={`hemWidth, 54\nchestWidth, 56\nshoulderWidth, 45\n…`}
              style={{ width: '100%', fontFamily: 'monospace', fontSize: 11, padding: 5, border: '1px solid #cbd5e1', borderRadius: 4, boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 5, marginTop: 5 }}>
              <label style={{ flex: 1, padding: '5px', background: '#1e293b', color: 'white', borderRadius: 4, cursor: 'pointer', fontSize: 10, textAlign: 'center' }}>
                📁 Upload file
                <input type="file" hidden accept=".csv,.json,.txt" onChange={handleImportFile} />
              </label>
              <button onClick={handleImportApply}
                style={{ flex: 1, padding: '5px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 'bold' }}>Apply</button>
            </div>
          </div>
        )}

        {Object.entries(groupedMeas).map(([grp, entries]) => (
          <div key={grp} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0', paddingBottom: 3, marginBottom: 6 }}>
              {grp} Measurements
            </div>
            {entries.map(([key, def]) => {
              const original = baseline[key] ?? def.default;
              const current  = measurements[key] ?? def.default;
              const delta    = current - original;
              return (
                <div key={key} style={{ marginBottom: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#475569', marginBottom: 2 }}>
                    <span title={key}>{def.label}</span>
                    <span style={{ color: delta > 0 ? '#059669' : delta < 0 ? '#dc2626' : '#94a3b8', fontFamily: 'monospace' }}>
                      {original.toFixed(1)} → {current.toFixed(1)} ({delta >= 0 ? '+' : ''}{delta.toFixed(1)}) {def.unit}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                    <input type="range" min={def.min} max={def.max} step={0.5} value={current}
                      onChange={e => setMeasurements(prev => ({ ...prev, [key]: parseFloat(e.target.value) }))} style={{ flex: 1 }} />
                    <input type="number" min={def.min} max={def.max} step={0.1} value={current}
                      onChange={e => setMeasurements(prev => ({ ...prev, [key]: parseFloat(e.target.value) || 0 }))}
                      style={{ width: 54, padding: '2px 4px', border: '1px solid #e2e8f0', borderRadius: 3, fontSize: 11, textAlign: 'right' }} />
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {/* Seam Allowance panel */}
        <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 6, padding: 8, marginBottom: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#9f1239', fontWeight: 'bold' }}>
            <input type="checkbox" checked={saEnabled} onChange={e => setSaEnabled(e.target.checked)} />
            ✂ Seam Allowance (cut pattern)
          </label>
          {saEnabled && (
            <div style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 10, color: '#7f1d1d' }}>SA:</span>
              <input type="range" min={0.3} max={3} step={0.1} value={saCm}
                onChange={e => setSaCm(parseFloat(e.target.value))} style={{ flex: 1 }} />
              <input type="number" min={0} max={5} step={0.1} value={saCm}
                onChange={e => setSaCm(parseFloat(e.target.value) || 0)}
                style={{ width: 44, padding: '2px 4px', border: '1px solid #fecdd3', borderRadius: 3, fontSize: 11 }} />
              <span style={{ fontSize: 10, color: '#7f1d1d' }}>cm</span>
            </div>
          )}
          <div style={{ fontSize: 9, color: '#7f1d1d', marginTop: 4, opacity: 0.85 }}>
            Spec sheet measures the <b>finished</b> garment. The cut piece is offset by the seam allowance.
          </div>
        </div>

        <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 6, padding: 8, marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: '#92400e', fontWeight: 'bold', marginBottom: 4 }}>ORIGINAL vs CURRENT</div>
          <div style={{ display: 'flex', gap: 5 }}>
            <button onClick={rebaseline} style={{ flex: 1, padding: 5, background: '#f59e0b', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>Set as Original</button>
            <button onClick={resetToBaseline} style={{ flex: 1, padding: 5, background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>Reset</button>
          </div>
        </div>
      </div>

      {/* RIGHT: preview + actions */}
      <div style={{ display: 'flex', flexDirection: 'column', padding: 10, overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#475569' }}>
            <input type="checkbox" checked={showDims} onChange={e => setShowDims(e.target.checked)} /> Show Dims
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#475569' }}>
            Zoom <input type="range" min={0.5} max={2} step={0.05} value={zoom} onChange={e => setZoom(parseFloat(e.target.value))} style={{ width: 100 }} />
            <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{zoom.toFixed(2)}×</span>
          </label>
          <div style={{ flex: 1 }} />
          {saveBadge && <span style={{ fontSize: 10, background: '#dcfce7', color: '#166534', padding: '3px 8px', borderRadius: 10 }}>✓ Saved</span>}
          {aiMsg && <span style={{ fontSize: 10, background: '#eff6ff', color: '#1e40af', padding: '3px 8px', borderRadius: 10 }}>{aiMsg}</span>}
        </div>

        {/* Node edit mode toggle */}
        <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:6 }}>
          <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:11,
            color: nodeEditMode ? '#7c3aed' : '#475569',
            background: nodeEditMode ? '#ede9fe' : '#f1f5f9',
            padding:'4px 10px', borderRadius:6, cursor:'pointer', userSelect:'none' }}>
            <input type="checkbox" checked={nodeEditMode} onChange={e=>{setNodeEditMode(e.target.checked); if(!e.target.checked) setNodeOverrides({});}}/>
            🔵 Node Edit — drag vertices to reshape
          </label>
          {nodeEditMode && Object.keys(nodeOverrides).length>0 && (
            <button onClick={()=>setNodeOverrides({})}
              style={{padding:'4px 10px',background:'#fef2f2',color:'#dc2626',border:'1px solid #fecaca',borderRadius:5,cursor:'pointer',fontSize:10}}>
              ↺ Reset nodes
            </button>
          )}
          {nodeEditMode && Object.keys(nodeOverrides).length>0 && (
            <span style={{fontSize:9,color:'#7c3aed'}}>{Object.keys(nodeOverrides).length} node(s) overridden · will export as traced shape</span>
          )}
        </div>
        <div style={{ flex: 1, background: '#f8fafc', border: `1px ${nodeEditMode?'solid #c4b5fd':'dashed #cbd5e1'}`, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto', padding: 10 }}>
          {Object.keys(measurements).length > 0 && (
            nodeEditMode
              ? <ShapeEditor template={template} measurements={measurements} showDims={showDims}
                  zoom={zoom} saPath={saPath}
                  nodeOverrides={nodeOverrides}
                  onNodeOverride={(idx, delta) => setNodeOverrides(prev => ({...prev, [idx]: delta}))}/>
              : <ShapePreview template={template} measurements={measurements} showDims={showDims} zoom={zoom} saPath={saPath} />
          )}
        </div>

        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
          <button onClick={commitShape}
            style={{ padding: '8px 14px', background: '#059669', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 5 }}>
            <Save size={13} /> Save Shape to Part
          </button>
          <button onClick={runAiShape} disabled={aiBusy}
            style={{ padding: '8px 14px', background: aiBusy ? '#818cf8' : '#6366f1', color: 'white', border: 'none', borderRadius: 6, cursor: aiBusy ? 'wait' : 'pointer', fontSize: 12, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 5 }}>
            <Wand2 size={13} /> {aiBusy ? 'Asking AI…' : 'AI: Interpret'}
          </button>
          <div style={{ flex: 1 }} />
          <div style={{ fontSize: 10, color: '#94a3b8', alignSelf: 'center' }}>
            {Object.keys(tpl.measurements).length} inputs · {tpl.name}{saEnabled ? ` · SA ${saCm}cm` : ''}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
//  AIPartDecomposer
// ----------------------------------------------------------------------------
//  Upload a full-garment sketch → backend vision model returns a list of the
//  panel pieces the garment is likely composed of (with suggested template
//  + rough measurements). The user reviews, edits, and saves each as a Part.
//
//  Backend contract for POST /api/ai_decompose.php:
//    Request:  { image: "data:image/png;base64,...", hint?: "knitted sweater" }
//    Response: { parts: [{name, template, measurements, confidence}] }
// ============================================================================
function AIPartDecomposer({ onPartsDetected, initialHint = '' }) {
  const [image, setImage] = useState('');
  const [hint, setHint]   = useState(initialHint);
  const [busy, setBusy]   = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr]     = useState('');

  const handleFile = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    const b64 = await new Promise((res) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f);
    });
    setImage(b64);
  };

  const runDecompose = async () => {
    if (!image) { alert('Upload a garment sketch first'); return; }
    setBusy(true); setErr(''); setResult(null);
    try {
      const res = await fetch(API.aiDecomposeGarment, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image, hint })
      });
      const txt = await res.text();
      let json;
      try { json = JSON.parse(txt); } catch (_) { json = { raw: txt }; }
      if (json.parts && Array.isArray(json.parts)) {
        setResult(json.parts);
      } else if (json.error) {
        setErr(json.error);
      } else {
        // graceful stub - offer a default skeleton so the UX still works
        setErr('AI endpoint not configured. Using default t-shirt skeleton as a starting point.');
        setResult([
          { name: 'Front',   template: 'tee_front', measurements: {}, confidence: 0 },
          { name: 'Back',    template: 'tee_back',  measurements: {}, confidence: 0 },
          { name: 'Sleeve L',template: 'sleeve',    measurements: {}, confidence: 0 },
          { name: 'Sleeve R',template: 'sleeve',    measurements: {}, confidence: 0 },
        ]);
      }
    } catch (e) {
      setErr('Network error: ' + e.message);
    } finally {
      setBusy(false);
    }
  };

  const acceptAll = () => {
    if (!result) return;
    onPartsDetected(result.map(p => ({
      ...p,
      id: `ai-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      sourceImage: image,
    })));
    setResult(null); setImage('');
  };

  return (
    <div style={{ background: 'white', padding: 16, borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
      <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1e293b', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Brain size={16} color="#8b5cf6" /> AI Part Decomposer
      </div>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
        Upload a sketch or photo of a complete garment — AI suggests which panel pieces it consists of.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
        {/* Upload */}
        <div style={{ border: '2px dashed #cbd5e1', borderRadius: 8, minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', position: 'relative' }}>
          {image ? (
            <>
              <img src={image} alt="garment" style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain' }} />
              <button onClick={() => setImage('')} style={{ position: 'absolute', top: 6, right: 6, background: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer' }}>
                <X size={12} />
              </button>
            </>
          ) : (
            <label style={{ cursor: 'pointer', textAlign: 'center', color: '#94a3b8', padding: 30 }}>
              <Upload size={32} style={{ margin: '0 auto 8px' }} />
              <div style={{ fontSize: 12 }}>Upload garment sketch / photo</div>
              <input type="file" accept="image/*" hidden onChange={handleFile} />
            </label>
          )}
        </div>

        {/* Controls */}
        <div>
          <label style={{ fontSize: 10, color: '#64748b', fontWeight: 'bold' }}>HINT (optional)</label>
          <input value={hint} onChange={e => setHint(e.target.value)}
            placeholder="e.g. knitted cardigan, raglan sleeves"
            style={{ width: '100%', padding: 7, border: '1px solid #e2e8f0', borderRadius: 5, fontSize: 12, marginBottom: 8, boxSizing: 'border-box' }} />
          <button onClick={runDecompose} disabled={busy || !image}
            style={{ width: '100%', padding: '10px', background: busy ? '#a78bfa' : '#8b5cf6', color: 'white', border: 'none', borderRadius: 6, cursor: busy || !image ? 'wait' : 'pointer', fontWeight: 'bold', fontSize: 12 }}>
            {busy ? 'Analysing…' : '🔍 Detect Parts'}
          </button>
          {err && <div style={{ marginTop: 8, fontSize: 11, color: '#dc2626', background: '#fee2e2', padding: 6, borderRadius: 4 }}>{err}</div>}
        </div>
      </div>

      {result && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: 10, background: '#f8fafc' }}>
          <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 6 }}>Detected parts ({result.length})</div>
          <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: 'white' }}>
              <th style={{ textAlign: 'left', padding: 4 }}>Name</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Template</th>
              <th style={{ textAlign: 'right', padding: 4 }}>Confidence</th>
            </tr></thead>
            <tbody>
              {result.map((p, i) => (
                <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
                  <td style={{ padding: 4 }}>{p.name}</td>
                  <td style={{ padding: 4, color: '#64748b' }}>{p.template}</td>
                  <td style={{ padding: 4, textAlign: 'right', color: '#059669' }}>{((p.confidence || 0) * 100).toFixed(0)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={acceptAll}
            style={{ width: '100%', marginTop: 8, padding: 8, background: '#059669', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 'bold', fontSize: 12 }}>
            ✓ Accept All → Add to Parts List
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
//  AITrainingUploader
// ----------------------------------------------------------------------------
//  Upload training triples (sketch, parts JSON, specs JSON) to teach the
//  decomposer. Stored server-side and used for fine-tuning / few-shot.
//
//  Backend contract for POST /api/ai_training.php:
//    { image, parts_json, specs_json, garment_type, labelled_by }
//    -> { id, total_samples }
// ============================================================================
function AITrainingUploader() {
  const [image, setImage]       = useState('');
  const [partsJson, setPartsJson] = useState('[\n  { "name": "Front", "template": "tee_front" },\n  { "name": "Back",  "template": "tee_back" }\n]');
  const [specsJson, setSpecsJson] = useState('{\n  "chestWidth": 54,\n  "bodyLength": 45\n}');
  const [garmentType, setGarmentType] = useState('T-Shirt');
  const [labelledBy, setLabelledBy]   = useState('');
  const [busy, setBusy]        = useState(false);
  const [result, setResult]    = useState('');

  const handleFile = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    const b64 = await new Promise((res) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f);
    });
    setImage(b64);
  };

  const upload = async () => {
    if (!image) { alert('Upload a garment image first'); return; }
    let pj, sj;
    try { pj = JSON.parse(partsJson); } catch (e) { alert('Invalid Parts JSON'); return; }
    try { sj = JSON.parse(specsJson); } catch (e) { alert('Invalid Specs JSON'); return; }

    setBusy(true); setResult('');
    try {
      const res = await fetch(API.aiTrainingUpload, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image, parts_json: pj, specs_json: sj, garment_type: garmentType, labelled_by: labelledBy
        })
      });
      const txt = await res.text();
      let j; try { j = JSON.parse(txt); } catch (_) { j = { raw: txt }; }
      if (j.id) {
        setResult(`✓ Uploaded. Training sample #${j.id}. Total samples: ${j.total_samples ?? '?'}`);
        setImage('');
      } else {
        setResult('Upload returned no id. Server said: ' + (j.error || j.raw || JSON.stringify(j)));
      }
    } catch (e) {
      setResult('Network error: ' + e.message);
    } finally { setBusy(false); }
  };

  return (
    <div style={{ background: 'white', padding: 16, borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
      <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1e293b', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Upload size={16} color="#059669" /> AI Training Data Uploader
      </div>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
        Upload labelled triples (garment image + parts breakdown + spec sheet). Each triple improves the decomposer.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr 1fr', gap: 10 }}>
        <div style={{ border: '2px dashed #cbd5e1', borderRadius: 8, minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
          {image ? <img src={image} alt="training" style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain' }} /> :
            <label style={{ cursor: 'pointer', textAlign: 'center', color: '#94a3b8', padding: 20 }}>
              <Upload size={28} style={{ margin: '0 auto 6px' }} />
              <div style={{ fontSize: 11 }}>Garment image</div>
              <input type="file" accept="image/*" hidden onChange={handleFile} />
            </label>}
        </div>
        <div>
          <label style={{ fontSize: 10, color: '#64748b', fontWeight: 'bold' }}>PARTS JSON</label>
          <textarea rows={10} value={partsJson} onChange={e => setPartsJson(e.target.value)}
            style={{ width: '100%', fontFamily: 'monospace', fontSize: 10, padding: 5, border: '1px solid #e2e8f0', borderRadius: 4, boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: '#64748b', fontWeight: 'bold' }}>SPECS JSON</label>
          <textarea rows={10} value={specsJson} onChange={e => setSpecsJson(e.target.value)}
            style={{ width: '100%', fontFamily: 'monospace', fontSize: 10, padding: 5, border: '1px solid #e2e8f0', borderRadius: 4, boxSizing: 'border-box' }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
        <input placeholder="Garment type (e.g. T-Shirt)" value={garmentType} onChange={e => setGarmentType(e.target.value)}
          style={{ padding: 6, border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 12 }} />
        <input placeholder="Labelled by" value={labelledBy} onChange={e => setLabelledBy(e.target.value)}
          style={{ padding: 6, border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 12 }} />
        <div style={{ flex: 1 }} />
        <button onClick={upload} disabled={busy}
          style={{ padding: '8px 16px', background: busy ? '#86efac' : '#059669', color: 'white', border: 'none', borderRadius: 5, cursor: busy ? 'wait' : 'pointer', fontWeight: 'bold', fontSize: 12 }}>
          {busy ? 'Uploading…' : '⬆ Upload Training Sample'}
        </button>
      </div>
      {result && <div style={{ marginTop: 8, fontSize: 11, padding: 6, background: result.startsWith('✓') ? '#dcfce7' : '#fee2e2', color: result.startsWith('✓') ? '#166534' : '#991b1b', borderRadius: 4 }}>{result}</div>}
    </div>
  );
}

// ============================================================================
//  PartsList — local working set of parts the user is building up
// ============================================================================
function PartsList({ parts, onEdit, onDelete, onAddManual, onPromoteToPartDNA }) {
  return (
    <div style={{ background: 'white', padding: 16, borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Package size={16} color="#2563eb" /> Working Parts ({parts.length})
        </div>
        <button onClick={onAddManual}
          style={{ padding: '6px 12px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 'bold' }}>
          + Add Part Manually
        </button>
      </div>
      {parts.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 12, background: '#f8fafc', borderRadius: 6 }}>
          No parts yet. Use AI Decomposer above or click "+ Add Part Manually".
        </div>
      ) : (
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#f1f5f9' }}>
            <th style={{ textAlign: 'left', padding: 6 }}>Name</th>
            <th style={{ textAlign: 'left', padding: 6 }}>Template</th>
            <th style={{ textAlign: 'right', padding: 6 }}>W × H (cm)</th>
            <th style={{ textAlign: 'right', padding: 6 }}>Area (cm²)</th>
            <th style={{ textAlign: 'right', padding: 6 }}>SA</th>
            <th style={{ textAlign: 'center', padding: 6 }}>Actions</th>
          </tr></thead>
          <tbody>
            {parts.map((p, i) => {
              const tpl = GARMENT_TEMPLATES[p.template];
              let bbox = { width: 0, height: 0 }, area = 0;
              if (tpl && p.measurements) {
                try {
                  const shape = tpl.build(p.measurements);
                  bbox = { width: shape.width - 20, height: shape.height - 20 };
                  area = polygonArea(shape.polyline);
                } catch (_) {}
              }
              return (
                <tr key={p.id || i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: 6 }}><b>{p.name || `Part ${i+1}`}</b></td>
                  <td style={{ padding: 6, color: '#64748b' }}>{tpl?.icon} {tpl?.name || p.template}</td>
                  <td style={{ padding: 6, textAlign: 'right', fontFamily: 'monospace' }}>{bbox.width.toFixed(1)} × {bbox.height.toFixed(1)}</td>
                  <td style={{ padding: 6, textAlign: 'right', fontFamily: 'monospace' }}>{area.toFixed(0)}</td>
                  <td style={{ padding: 6, textAlign: 'right', color: '#dc2626' }}>{p.seamAllowanceCm ? `${p.seamAllowanceCm}cm` : '–'}</td>
                  <td style={{ padding: 6, textAlign: 'center' }}>
                    <button onClick={() => onEdit(i)} title="Edit" style={{ padding: 3, background: '#fef3c7', border: 'none', borderRadius: 3, cursor: 'pointer', marginRight: 4 }}><Edit2 size={11} /></button>
                    <button onClick={() => onPromoteToPartDNA(i)} title="Save to Part DNA library" style={{ padding: 3, background: '#dcfce7', border: 'none', borderRadius: 3, cursor: 'pointer', marginRight: 4 }}><Save size={11} /></button>
                    <button onClick={() => onDelete(i)} title="Remove" style={{ padding: 3, background: '#fee2e2', border: 'none', borderRadius: 3, cursor: 'pointer' }}><Trash2 size={11} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ============================================================================
//  PartEditor — inline editor, reuses SpecDrivenShape for a single part
// ============================================================================
function PartEditor({ part, onSave, onCancel }) {
  const [local, setLocal] = useState({ ...part });
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: 10, width: '90vw', height: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 12, borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 'bold' }}>
            <input value={local.name || ''} onChange={e => setLocal(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Part name" style={{ padding: 6, fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 4 }} />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={onCancel} style={{ padding: '6px 12px', background: '#f1f5f9', border: 'none', borderRadius: 5, cursor: 'pointer' }}>Cancel</button>
            <button onClick={() => onSave(local)} style={{ padding: '6px 12px', background: '#059669', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 'bold' }}>Save</button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <SpecDrivenShape
            onSave={() => {}} // irrelevant in editor – we only care about specs
            initialTemplate={local.template}
            initialSpecs={{ measurements: local.measurements, baseline: local.baseline }}
            onSpecsChange={(specs) => setLocal(prev => ({ ...prev, ...specs }))}
          />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
//  GarmentAssembly — visualise all parts laid out as a full garment
// ----------------------------------------------------------------------------
//  Simple approach: arrange parts in a grid with their natural orientation.
//  Drag-arrange would be nice but ships as a later enhancement.
// ============================================================================
function GarmentAssembly({ parts, garmentName, setGarmentName, onSaveAsStyle }) {
  const shapes = useMemo(() => parts.map((p, idx) => {
    const tpl = GARMENT_TEMPLATES[p.template];
    if (!tpl || !p.measurements) return null;
    try {
      const s = tpl.build(p.measurements);
      return { idx, part: p, shape: s };
    } catch (e) { return null; }
  }).filter(Boolean), [parts]);

  // Simple row-based layout for display
  const GAP = 8;
  let rowMaxH = 0, rowX = 0, rowY = 0;
  const MAX_ROW_W = 600;
  const positioned = shapes.map(s => {
    if (rowX + s.shape.width > MAX_ROW_W) {
      rowY += rowMaxH + GAP;
      rowX = 0; rowMaxH = 0;
    }
    const pos = { x: rowX, y: rowY };
    rowX += s.shape.width + GAP;
    if (s.shape.height > rowMaxH) rowMaxH = s.shape.height;
    return { ...s, pos };
  });
  const totalW = Math.max(MAX_ROW_W, ...positioned.map(p => p.pos.x + p.shape.width));
  const totalH = rowY + rowMaxH;

  return (
    <div style={{ background: 'white', padding: 16, borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
      <div style={{ fontSize: 13, fontWeight: 'bold', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Layers size={16} color="#f97316" /> Garment Assembly – Combined View
      </div>
      {parts.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13, background: '#f8fafc', borderRadius: 6 }}>
          Add parts first. The combined garment layout will appear here.
        </div>
      ) : (
        <>
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'auto', padding: 10, marginBottom: 10 }}>
            <svg viewBox={`0 0 ${totalW + 20} ${totalH + 20}`} width={Math.min(totalW + 20, 1200)} style={{ background: 'white' }}>
              {positioned.map(({ idx, part, shape, pos }) => (
                <g key={idx} transform={`translate(${pos.x + 10}, ${pos.y + 10})`}>
                  <g transform={`translate(${shape.width/2}, ${shape.height}) scale(1,-1)`}>
                    <path d={shape.path} fill="rgba(59,130,246,0.08)" stroke="#1e293b" strokeWidth={0.5} />
                  </g>
                  <text x={shape.width/2} y={shape.height + 4} fontSize="3" textAnchor="middle" fill="#1e293b">
                    {part.name || GARMENT_TEMPLATES[part.template]?.name}
                  </text>
                </g>
              ))}
            </svg>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input placeholder="Garment / Style name" value={garmentName} onChange={e => setGarmentName(e.target.value)}
              style={{ flex: 1, padding: 8, border: '1px solid #e2e8f0', borderRadius: 5, fontSize: 12 }} />
            <button onClick={onSaveAsStyle}
              style={{ padding: '8px 14px', background: '#f97316', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 'bold', fontSize: 12 }}>
              <Save size={13} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
              Save as Garment / Style
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
//  SpreadLayoutTool — fabric marker-making + yield calculator + export
// ============================================================================
function SpreadLayoutTool({ parts }) {
  // Fabric settings
  const [fabricWidth, setFabricWidth]   = useState(140);   // cm (56" ≈ 142 cm)
  const [fabricLength, setFabricLength] = useState(1250);  // cm (≈ 13Yd22In)
  const [unit, setUnit]                 = useState('cm');
  const [allowRotate, setAllowRotate]   = useState(true);
  const [gap, setGap]                   = useState(0.5);   // buffer between parts (cm)
  const [gridStep, setGridStep]         = useState(3);     // cm resolution
  const [iterations, setIterations]     = useState(1);     // multi-pass optimize

  // Size mix — which sizes, and how many of each full garment
  const [sizes, setSizes] = useState([
    { code: 'S',   count: 1, scale: 0.92 },
    { code: 'M',   count: 2, scale: 1.00 },
    { code: 'L',   count: 2, scale: 1.04 },
    { code: 'XL',  count: 2, scale: 1.08 },
    { code: 'XXL', count: 1, scale: 1.12 },
  ]);

  const [layout, setLayout] = useState(null);
  const [placements, setPlacements] = useState([]); // editable copy
  const [dragMode, setDragMode]     = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [drag, setDrag]             = useState(null);   // {idx, offsetX, offsetY}
  const [packing, setPacking]       = useState(false);
  const [trainingMsg, setTrainingMsg] = useState('');
  const svgRef = useRef(null);

  // Build the expanded parts list: every garment in the size mix contributes
  // all of its parts, each one scaled by the size's scale factor.
  const expanded = useMemo(() => {
    const list = [];
    sizes.forEach(sz => {
      for (let i = 0; i < sz.count; i++) {
        parts.forEach((p, pi) => {
          const tpl = GARMENT_TEMPLATES[p.template];
          if (!tpl || !p.measurements) return;
          const scaledMeas = {};
          Object.entries(p.measurements).forEach(([k, v]) => { scaledMeas[k] = v * sz.scale; });
          const shape = tpl.build(scaledMeas);
          // Include seam allowance if part has it
          let outline = shape.polyline;
          if (p.seamAllowanceCm && p.seamAllowanceCm > 0) {
            try { outline = offsetPolygon(shape.polyline, p.seamAllowanceCm); } catch (_) {}
          }
          const bb = polylineBBox(outline);
          list.push({
            id: `${sz.code}-${i}-${pi}`,
            name: `${p.name}·${sz.code}`,
            size: sz.code,
            template: p.template,
            measurements: scaledMeas,
            outline, polyline: outline,
            bbox: { width: bb.width, height: bb.height },
            polyArea: polygonArea(outline),
          });
        });
      }
    });
    return list;
  }, [parts, sizes]);

  const runPack = async () => {
    if (expanded.length === 0) { alert('No parts to lay out. Build parts first.'); return; }
    setPacking(true);
    // Defer so the button visibly updates
    await new Promise(r => setTimeout(r, 30));
    try {
      const res = packPartsIntoSpread(expanded, fabricLength, fabricWidth, {
        allowRotate, margin: gap, gridStep, iterations
      });
      setLayout(res);
      setPlacements(res.placements.map(p => ({ ...p })));
      setSelectedIdx(null);
    } finally { setPacking(false); }
  };

  // Recompute yield / used-length whenever user drags parts around
  const liveMetrics = useMemo(() => {
    if (!layout || placements.length === 0) return null;
    const usedLength = placements.reduce((m, p) => Math.max(m, p.x + p.bboxW), 0);
    return {
      ...layout, placements, usedLength,
      placedCount: placements.length,
    };
  }, [layout, placements]);

  // Colour per size
  const sizeColors = { S: '#fecaca', M: '#bfdbfe', L: '#bbf7d0', XL: '#fde68a', XXL: '#ddd6fe' };

  // ---- Drag / manipulation handlers ----
  const svgToFabric = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = clientX; pt.y = clientY;
    const c = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: c.x, y: c.y };
  };

  const onPartMouseDown = (e, idx) => {
    if (!dragMode) { setSelectedIdx(idx); return; }
    e.stopPropagation();
    const { x: mx, y: my } = svgToFabric(e.clientX, e.clientY);
    const pl = placements[idx];
    setDrag({ idx, offsetX: mx - pl.x, offsetY: my - pl.y });
    setSelectedIdx(idx);
  };

  const onSvgMouseMove = (e) => {
    if (!drag) return;
    const { x: mx, y: my } = svgToFabric(e.clientX, e.clientY);
    let nx = mx - drag.offsetX, ny = my - drag.offsetY;
    // Clamp to fabric
    const pl = placements[drag.idx];
    nx = Math.max(0, Math.min(fabricLength - pl.bboxW, nx));
    ny = Math.max(0, Math.min(fabricWidth  - pl.bboxH, ny));
    setPlacements(prev => prev.map((p, i) => i === drag.idx
      ? { ...p, x: nx, y: ny, worldPoly: placePolyline(p.localPoly, nx, ny) }
      : p
    ));
  };

  const onSvgMouseUp = () => {
    if (!drag) return;
    // Detect collisions; if the dragged part overlaps another, flag it (we
    // don't snap-back — the user might be mid-adjustment. They see the red
    // border and fix it.)
    setDrag(null);
  };

  // Rotate selected part 90° on 'R' keypress
  useEffect(() => {
    const onKey = (e) => {
      if (selectedIdx === null) return;
      if (e.key !== 'r' && e.key !== 'R') return;
      setPlacements(prev => prev.map((p, i) => {
        if (i !== selectedIdx) return p;
        const newAngle = (p.rotation + 90) % 360;
        // Get the original part's polyline and re-normalize at the new angle
        const part = expanded.find(x => x.id === p.partId);
        if (!part) return p;
        const norm = normalizePolyline(part.polyline, newAngle);
        // Keep the same x,y but clamp to fabric
        const nx = Math.min(fabricLength - norm.width, p.x);
        const ny = Math.min(fabricWidth - norm.height, p.y);
        return {
          ...p,
          rotation: newAngle,
          localPoly: norm.poly,
          worldPoly: placePolyline(norm.poly, nx, ny),
          bboxW: norm.width, bboxH: norm.height,
          x: nx, y: ny,
        };
      }));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedIdx, expanded, fabricLength, fabricWidth]);

  // Compute which placements currently overlap (for visual warning)
  const overlapFlags = useMemo(() => {
    const flags = new Array(placements.length).fill(false);
    for (let i = 0; i < placements.length; i++) {
      for (let j = i + 1; j < placements.length; j++) {
        if (polygonsOverlap(placements[i].worldPoly, placements[j].worldPoly, 0)) {
          flags[i] = true; flags[j] = true;
        }
      }
    }
    return flags;
  }, [placements]);

  const outOfBoundsFlags = useMemo(() => placements.map(p => {
    for (const [x, y] of p.worldPoly) {
      if (x < 0 || x > fabricLength || y < 0 || y > fabricWidth) return true;
    }
    return false;
  }), [placements, fabricLength, fabricWidth]);

  const anyProblem = overlapFlags.some(Boolean) || outOfBoundsFlags.some(Boolean);

  // HD PNG export of the spread
  const exportPng = async () => {
    if (!svgRef.current) return;
    const svgStr = new XMLSerializer().serializeToString(svgRef.current);
    const png = await svgToPng(svgStr, 2400, Math.round(2400 * (fabricWidth / fabricLength)));
    const a = document.createElement('a');
    a.href = png; a.download = `spread_${fabricLength}x${fabricWidth}.png`; a.click();
  };

  // PDF export via browser print
  const exportPdf = () => {
    if (!liveMetrics) { alert('Run the layout first'); return; }
    const svgStr = svgRef.current ? new XMLSerializer().serializeToString(svgRef.current) : '';
    const html = `<!DOCTYPE html><html><head><title>Spread Layout</title>
      <style>
        @page { size: A3 landscape; margin: 10mm; }
        body { margin:0; font-family: system-ui, sans-serif; }
        h2 { margin: 0 0 4mm; font-size: 14pt; }
        .meta { font-size: 10pt; margin-bottom: 6mm; }
        .meta b { display: inline-block; min-width: 32mm; }
        .sheet { page-break-after: always; }
        svg { width: 100%; height: auto; border: 1px solid #ccc; }
      </style></head><body>
      <div class="sheet">
        <h2>Spread / Marker Layout</h2>
        <div class="meta">
          <div><b>Fabric:</b> ${fabricLength} × ${fabricWidth} ${unit}  (Area ${(fabricLength * fabricWidth).toLocaleString()} ${unit}²)</div>
          <div><b>Used length:</b> ${liveMetrics.usedLength.toFixed(1)} ${unit}  |  <b>Placed parts:</b> ${liveMetrics.placedCount} / ${liveMetrics.totalCount}</div>
          <div><b>Yield (utilisation):</b> <span style="color:#059669;font-weight:bold">${liveMetrics.yieldPct.toFixed(2)}%</span></div>
          <div><b>Size mix:</b> ${sizes.filter(s => s.count > 0).map(s => `${s.count}×${s.code}`).join(' + ')}</div>
          <div><b>Generated:</b> ${new Date().toLocaleString()}</div>
        </div>
        ${svgStr}
      </div>
      <script>window.onload=()=>setTimeout(()=>window.print(),300);</script>
      </body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html); w.document.close();
  };

  // Save manual layout as AI training sample
  const saveAsTraining = async () => {
    if (!liveMetrics || placements.length === 0) { alert('Run and adjust the layout first'); return; }
    if (anyProblem) {
      if (!window.confirm('This layout has overlaps or out-of-bounds parts. Save anyway?')) return;
    }
    setTrainingMsg('Saving…');
    // Render current marker as PNG for labelled training
    const svgStr = svgRef.current ? new XMLSerializer().serializeToString(svgRef.current) : '';
    const png = await svgToPng(svgStr, 1600, Math.round(1600 * (fabricWidth / fabricLength)));
    const payload = {
      kind: 'marker_layout',
      fabric: { length: fabricLength, width: fabricWidth, unit },
      size_mix: sizes.filter(s => s.count > 0),
      placements: placements.map(p => ({
        partId: p.partId, name: p.name, size: p.size,
        x: +p.x.toFixed(2), y: +p.y.toFixed(2),
        rotation: p.rotation,
        bboxW: +p.bboxW.toFixed(2), bboxH: +p.bboxH.toFixed(2),
        template: expanded.find(e => e.id === p.partId)?.template,
        measurements: expanded.find(e => e.id === p.partId)?.measurements,
      })),
      metrics: {
        yieldPct: +liveMetrics.yieldPct.toFixed(2),
        usedLength: +liveMetrics.usedLength.toFixed(2),
        placedCount: liveMetrics.placedCount,
      },
      image: png,
      labelled_by: 'user',
      labelled_on: new Date().toISOString(),
    };
    try {
      const res = await fetch(API.aiTrainingUpload, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const j = await res.json().catch(() => ({}));
      if (j.id) {
        setTrainingMsg(`✓ Saved as training sample #${j.id} (total: ${j.total_samples ?? '?'})`);
      } else {
        setTrainingMsg('Saved locally (backend not configured).');
        // Fall-back: download JSON so nothing is lost
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `marker_training_${Date.now()}.json`; a.click();
      }
    } catch (e) {
      setTrainingMsg('Network error — downloaded JSON locally.');
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `marker_training_${Date.now()}.json`; a.click();
    }
    setTimeout(() => setTrainingMsg(''), 6000);
  };

  // Visual
  const SCALE = 900 / Math.max(fabricLength, 1);
  const dispW = Math.min(fabricLength * SCALE, 1200);
  const dispH = dispW * (fabricWidth / fabricLength);

  return (
    <div style={{ background: 'white', padding: 16, borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
      <div style={{ fontSize: 13, fontWeight: 'bold', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Grid size={16} color="#0891b2" /> Spread Layout / Marker Maker
        <span style={{ fontSize: 10, color: '#64748b', fontWeight: 'normal', marginLeft: 8 }}>
          polygon-aware • drag-to-improve • multi-pass optimise
        </span>
      </div>

      {/* Fabric settings */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8, marginBottom: 10, background: '#f0f9ff', padding: 8, borderRadius: 6 }}>
        <div>
          <label style={{ fontSize: 10, color: '#0c4a6e', fontWeight: 'bold' }}>FABRIC LENGTH ({unit})</label>
          <input type="number" value={fabricLength} onChange={e => setFabricLength(parseFloat(e.target.value) || 0)}
            style={{ width: '100%', padding: 5, border: '1px solid #bae6fd', borderRadius: 4, fontSize: 12, boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: '#0c4a6e', fontWeight: 'bold' }}>FABRIC WIDTH ({unit})</label>
          <input type="number" value={fabricWidth} onChange={e => setFabricWidth(parseFloat(e.target.value) || 0)}
            style={{ width: '100%', padding: 5, border: '1px solid #bae6fd', borderRadius: 4, fontSize: 12, boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: '#0c4a6e', fontWeight: 'bold' }}>UNIT</label>
          <select value={unit} onChange={e => setUnit(e.target.value)} style={{ width: '100%', padding: 5, border: '1px solid #bae6fd', borderRadius: 4, fontSize: 12 }}>
            <option value="cm">cm</option><option value="in">in</option><option value="mm">mm</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 10, color: '#0c4a6e', fontWeight: 'bold' }} title="Minimum buffer between parts">GAP ({unit})</label>
          <input type="number" step={0.1} value={gap} onChange={e => setGap(parseFloat(e.target.value) || 0)}
            style={{ width: '100%', padding: 5, border: '1px solid #bae6fd', borderRadius: 4, fontSize: 12, boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: '#0c4a6e', fontWeight: 'bold' }} title="Scan resolution for placement search. Smaller = tighter but slower.">GRID ({unit})</label>
          <input type="number" step={0.5} min={0.5} max={10} value={gridStep} onChange={e => setGridStep(parseFloat(e.target.value) || 2)}
            style={{ width: '100%', padding: 5, border: '1px solid #bae6fd', borderRadius: 4, fontSize: 12, boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: '#0c4a6e', fontWeight: 'bold' }} title="Multi-pass shuffle count (best result wins)">OPTIMIZE PASSES</label>
          <input type="number" min={1} max={50} value={iterations} onChange={e => setIterations(parseInt(e.target.value) || 1)}
            style={{ width: '100%', padding: 5, border: '1px solid #bae6fd', borderRadius: 4, fontSize: 12, boxSizing: 'border-box' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
        <button onClick={runPack} disabled={packing}
          style={{ padding: '8px 16px', background: packing ? '#67e8f9' : '#0891b2', color: 'white', border: 'none', borderRadius: 5, cursor: packing ? 'wait' : 'pointer', fontWeight: 'bold', fontSize: 12 }}>
          {packing ? 'Packing…' : '⚙ Run Layout'}
        </button>
        <label style={{ fontSize: 11, color: '#0c4a6e', display: 'flex', alignItems: 'center', gap: 5 }}>
          <input type="checkbox" checked={allowRotate} onChange={e => setAllowRotate(e.target.checked)} />
          Allow rotation (0/90/180/270°)
        </label>
        {liveMetrics && (
          <>
            <div style={{ width: 1, height: 24, background: '#e2e8f0', margin: '0 4px' }} />
            <button onClick={() => setDragMode(v => !v)}
              style={{ padding: '6px 14px', background: dragMode ? '#f59e0b' : '#f1f5f9', color: dragMode ? 'white' : '#475569',
                border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: dragMode ? 'bold' : 'normal' }}>
              {dragMode ? '🖐 Drag Mode ON' : 'Drag to Improve'}
            </button>
            {dragMode && (
              <span style={{ fontSize: 10, color: '#92400e', background: '#fef3c7', padding: '3px 8px', borderRadius: 10 }}>
                Click a part to select · drag to move · press <b>R</b> to rotate 90°
              </span>
            )}
          </>
        )}
      </div>

      {/* Size mix editor */}
      <div style={{ marginBottom: 10, background: '#fffbeb', padding: 8, borderRadius: 6 }}>
        <div style={{ fontSize: 10, color: '#92400e', fontWeight: 'bold', marginBottom: 6 }}>SIZE MIX (each row = garments of that size; scale deforms measurements)</div>
        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#fef3c7' }}>
            <th style={{ padding: 4, textAlign: 'left' }}>Size</th>
            <th style={{ padding: 4, textAlign: 'right' }}>Count</th>
            <th style={{ padding: 4, textAlign: 'right' }}>Scale Factor</th>
            <th></th>
          </tr></thead>
          <tbody>
            {sizes.map((s, i) => (
              <tr key={i}>
                <td style={{ padding: 2 }}>
                  <input value={s.code} onChange={e => setSizes(prev => prev.map((ps, pi) => pi === i ? { ...ps, code: e.target.value } : ps))}
                    style={{ width: 50, padding: 3, border: '1px solid #fde68a', borderRadius: 3, fontSize: 11 }} />
                </td>
                <td style={{ padding: 2, textAlign: 'right' }}>
                  <input type="number" min={0} value={s.count} onChange={e => setSizes(prev => prev.map((ps, pi) => pi === i ? { ...ps, count: parseInt(e.target.value) || 0 } : ps))}
                    style={{ width: 55, padding: 3, border: '1px solid #fde68a', borderRadius: 3, fontSize: 11, textAlign: 'right' }} />
                </td>
                <td style={{ padding: 2, textAlign: 'right' }}>
                  <input type="number" step={0.01} min={0.5} max={2} value={s.scale} onChange={e => setSizes(prev => prev.map((ps, pi) => pi === i ? { ...ps, scale: parseFloat(e.target.value) || 1 } : ps))}
                    style={{ width: 65, padding: 3, border: '1px solid #fde68a', borderRadius: 3, fontSize: 11, textAlign: 'right' }} />
                </td>
                <td style={{ padding: 2, textAlign: 'right' }}>
                  <button onClick={() => setSizes(prev => prev.filter((_, pi) => pi !== i))} style={{ padding: 2, background: '#fee2e2', border: 'none', borderRadius: 3, cursor: 'pointer' }}><X size={10} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button onClick={() => setSizes(prev => [...prev, { code: '?', count: 1, scale: 1 }])}
          style={{ marginTop: 5, padding: '4px 10px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 10 }}>+ Size</button>
        <span style={{ marginLeft: 10, fontSize: 11, color: '#92400e' }}>
          Total garments: <b>{sizes.reduce((s, x) => s + (x.count || 0), 0)}</b> ·
          Parts to pack: <b>{expanded.length}</b>
        </span>
      </div>

      {/* Layout render */}
      {liveMetrics && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 10 }}>
            <div style={{ background: '#f0fdf4', padding: 8, borderRadius: 5, textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: '#166534' }}>YIELD</div>
              <div style={{ fontSize: 18, fontWeight: 'bold', color: '#059669' }}>{liveMetrics.yieldPct.toFixed(2)}%</div>
            </div>
            <div style={{ background: '#eff6ff', padding: 8, borderRadius: 5, textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: '#1e40af' }}>USED LENGTH</div>
              <div style={{ fontSize: 18, fontWeight: 'bold', color: '#2563eb' }}>{liveMetrics.usedLength.toFixed(1)} {unit}</div>
            </div>
            <div style={{ background: '#fef3c7', padding: 8, borderRadius: 5, textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: '#92400e' }}>PLACED</div>
              <div style={{ fontSize: 18, fontWeight: 'bold', color: '#d97706' }}>{liveMetrics.placedCount}/{liveMetrics.totalCount}</div>
            </div>
            <div style={{ background: '#fce7f3', padding: 8, borderRadius: 5, textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: '#9f1239' }}>CONSUMPTION / GARMENT</div>
              <div style={{ fontSize: 18, fontWeight: 'bold', color: '#db2777' }}>
                {(liveMetrics.usedLength / Math.max(1, sizes.reduce((s, x) => s + x.count, 0))).toFixed(1)} {unit}
              </div>
            </div>
          </div>

          {anyProblem && (
            <div style={{ background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', padding: 6, borderRadius: 5, fontSize: 11, marginBottom: 6 }}>
              ⚠ Some parts overlap or lie outside the fabric. Red outlines mark the problem pieces.
            </div>
          )}

          <div style={{ overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fafafa', padding: 8, marginBottom: 8 }}>
            <svg ref={svgRef} viewBox={`0 0 ${fabricLength} ${fabricWidth}`}
              width={dispW} height={dispH}
              xmlns="http://www.w3.org/2000/svg"
              onMouseMove={onSvgMouseMove}
              onMouseUp={onSvgMouseUp}
              onMouseLeave={onSvgMouseUp}
              onClick={() => { if (!drag) setSelectedIdx(null); }}
              style={{ background: '#fff8e7', border: '2px solid #64748b', cursor: dragMode ? (drag ? 'grabbing' : 'grab') : 'default' }}
            >
              {/* Fabric grid - every 50 units */}
              {Array.from({ length: Math.floor(fabricLength / 50) + 1 }).map((_, i) => (
                <line key={`g${i}`} x1={i * 50} y1={0} x2={i * 50} y2={fabricWidth} stroke="#e2e8f0" strokeWidth={0.5} />
              ))}
              {Array.from({ length: Math.floor(fabricWidth / 50) + 1 }).map((_, i) => (
                <line key={`gh${i}`} x1={0} y1={i * 50} x2={fabricLength} y2={i * 50} stroke="#f1f5f9" strokeWidth={0.4} />
              ))}

              {/* --- REAL POLYGON PLACEMENTS --- */}
              {placements.map((pl, idx) => {
                const part = expanded.find(e => e.id === pl.partId);
                const sizeColor = sizeColors[part?.size] || '#e2e8f0';
                const pathD = polylineToPath(pl.localPoly);
                const isSelected = idx === selectedIdx;
                const isBad = overlapFlags[idx] || outOfBoundsFlags[idx];
                const stroke = isBad ? '#dc2626' : (isSelected ? '#2563eb' : '#334155');
                const strokeW = isBad ? 0.9 : (isSelected ? 0.8 : 0.35);
                return (
                  <g key={idx}
                     transform={`translate(${pl.x}, ${pl.y})`}
                     onMouseDown={(e) => onPartMouseDown(e, idx)}
                     style={{ cursor: dragMode ? 'grab' : 'pointer' }}>
                    <path d={pathD} fill={sizeColor} stroke={stroke} strokeWidth={strokeW}
                      opacity={isBad ? 0.5 : 0.85} strokeLinejoin="round" />
                    <text x={pl.bboxW / 2} y={pl.bboxH / 2}
                      fontSize={Math.min(pl.bboxW, pl.bboxH) * 0.25}
                      textAnchor="middle" dominantBaseline="central"
                      fill="#1e293b" fontWeight="bold" fontFamily="sans-serif"
                      style={{ pointerEvents: 'none' }}>
                      {part?.size || '?'}
                    </text>
                    {isSelected && (
                      <rect x={0} y={0} width={pl.bboxW} height={pl.bboxH}
                        fill="none" stroke="#2563eb" strokeWidth={0.3}
                        strokeDasharray="1.5,1" style={{ pointerEvents: 'none' }} />
                    )}
                  </g>
                );
              })}

              {/* Used length line */}
              <line x1={liveMetrics.usedLength} y1={0} x2={liveMetrics.usedLength} y2={fabricWidth}
                stroke="#dc2626" strokeWidth={1} strokeDasharray="4,2" />
              <text x={liveMetrics.usedLength + 2} y={10} fontSize={6} fill="#dc2626">USED {liveMetrics.usedLength.toFixed(1)}{unit}</text>
            </svg>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={exportPng}
              style={{ padding: '8px 14px', background: '#059669', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 'bold', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
              <Download size={13} /> HD PNG
            </button>
            <button onClick={exportPdf}
              style={{ padding: '8px 14px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 'bold', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
              <FileText size={13} /> PDF (via print)
            </button>
            <button onClick={saveAsTraining}
              style={{ padding: '8px 14px', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 'bold', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}
              title="Save this layout as labelled training data (your manual improvements teach the AI)">
              <Brain size={13} /> Save as Training Layout
            </button>
            {trainingMsg && <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 10, background: trainingMsg.startsWith('✓') ? '#dcfce7' : '#fef3c7', color: trainingMsg.startsWith('✓') ? '#166534' : '#92400e' }}>{trainingMsg}</span>}
            <div style={{ flex: 1 }} />
            <div style={{ alignSelf: 'center', fontSize: 11, color: '#64748b' }}>
              Projection-ready • {sizes.filter(s => s.count > 0).map(s => `${s.count}×${s.code}`).join(' + ')}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
//  DEFAULT EXPORT — top-level ShapeBuilder page
// ----------------------------------------------------------------------------
//  Top nav with 4 sub-tools. Owns the shared "parts" working set. Can read
//  existing parts from PartDNA db (db.masterParts) so you can pull a part
//  you already saved into the assembly/spread. Can write back via
//  props.onSavePartToDNA / props.onSaveGarmentAsStyle.
// ============================================================================
export default function ShapeBuilder({
  db, setDb,
  onSavePartToDNA,     // (part) => Promise<id>  – persists to master parts
  onSaveGarmentAsStyle,// (garment) => Promise<id>
  onBack,              // back to PartDNA
}) {
  const [view, setView]       = useState('decompose'); // decompose | parts | assembly | spread | training
  const [parts, setParts]     = useState([]);           // local working set
  const [editingIdx, setEditingIdx] = useState(null);
  const [garmentName, setGarmentName] = useState('');

  // Pull in parts that already exist in the master parts library
  const existingParts = db?.masterParts || [];

  const addManual = () => {
    setParts(prev => [...prev, {
      id: `local-${Date.now()}`, name: `Part ${prev.length + 1}`,
      template: 'tee_front', measurements: {}, baseline: {}, seamAllowanceCm: 0
    }]);
    setEditingIdx(parts.length);
  };

  const handleAiParts = (detected) => {
    // detected: [{name, template, measurements, confidence, sourceImage}]
    const enriched = detected.map(p => {
      // Fill in default measurements if AI didn't give any
      const tpl = GARMENT_TEMPLATES[p.template];
      const defaults = {};
      if (tpl) Object.entries(tpl.measurements).forEach(([k, d]) => { defaults[k] = d.default; });
      return {
        id: p.id || `ai-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: p.name, template: p.template,
        measurements: { ...defaults, ...(p.measurements || {}) },
        baseline: { ...defaults, ...(p.measurements || {}) },
        seamAllowanceCm: 0, sourceImage: p.sourceImage,
      };
    });
    setParts(prev => [...prev, ...enriched]);
    setView('parts');
  };

  const saveEdited = (updated) => {
    setParts(prev => prev.map((p, i) => i === editingIdx ? { ...p, ...updated } : p));
    setEditingIdx(null);
  };

  const deletePart = (idx) => {
    if (!window.confirm('Remove this part?')) return;
    setParts(prev => prev.filter((_, i) => i !== idx));
  };

  const promoteToPartDNA = async (idx) => {
    const p = parts[idx];
    if (!onSavePartToDNA) {
      alert('Save-to-PartDNA handler not wired. Returning to PartDNA manually, you can save from there.');
      return;
    }
    // Render the part to a PNG so it appears in the gallery
    const tpl = GARMENT_TEMPLATES[p.template];
    const svg = buildSpecSvgMarkup(p.template, p.measurements, true);
    const png = await svgToPng(svg, 800, 800);
    const partRecord = {
      name: p.name,
      garmentType: 'Component',
      designAttr: tpl?.name || p.template,
      image: png,
      specs: { template: p.template, measurements: p.measurements, baseline: p.baseline, seamAllowanceCm: p.seamAllowanceCm },
      processes: [],
      createdBy: 'ShapeBuilder',
    };
    try {
      const id = await onSavePartToDNA(partRecord);
      alert(`✓ "${p.name}" saved to Part DNA library${id ? ` (id: ${id})` : ''}`);
    } catch (e) {
      alert('Save failed: ' + e.message);
    }
  };

  const importFromPartDNA = (p) => {
    if (!p.specs?.template) {
      alert('This part was not built from a template, so its measurements cannot be imported into Shape Builder.');
      return;
    }
    setParts(prev => [...prev, {
      id: `imp-${Date.now()}`, name: p.name,
      template: p.specs.template, measurements: p.specs.measurements || {},
      baseline: p.specs.baseline || p.specs.measurements || {},
      seamAllowanceCm: p.specs.seamAllowanceCm || 0,
      sourceImage: p.image,
    }]);
  };

  const saveAsStyle = async () => {
    if (!garmentName) { alert('Give the garment a name first'); return; }
    if (parts.length === 0) { alert('No parts to save'); return; }
    if (!onSaveGarmentAsStyle) {
      alert('Save-as-style handler not wired — the garment is kept locally in this session.');
      return;
    }
    const garment = {
      name: garmentName,
      parts: parts.map(p => ({
        name: p.name, template: p.template, measurements: p.measurements, seamAllowanceCm: p.seamAllowanceCm
      })),
      createdBy: 'ShapeBuilder',
    };
    try {
      const id = await onSaveGarmentAsStyle(garment);
      alert(`✓ Garment saved as Style${id ? ` (id: ${id})` : ''}`);
      setGarmentName('');
    } catch (e) {
      alert('Save failed: ' + e.message);
    }
  };

  // ----------------- render -----------------
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#f8fafc', overflow: 'hidden' }}>
      {/* Top nav */}
      <div style={{ padding: '10px 16px', background: 'white', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={onBack} style={{ padding: '5px 10px', background: '#f1f5f9', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 11 }}>← Part DNA</button>
          <Scissors size={20} color="#8b5cf6" />
          <span style={{ fontWeight: 'bold', fontSize: '1.05rem', color: '#1e293b' }}>SHAPE BUILDER</span>
          <div style={{ display: 'flex', gap: 4, marginLeft: 10 }}>
            {[
              { id: 'decompose', label: '🧠 AI Decompose', },
              { id: 'parts',     label: '📦 Parts',        },
              { id: 'assembly',  label: '🧩 Assembly',     },
              { id: 'spread',    label: '📐 Spread Layout' },
              { id: 'training',  label: '🎓 AI Training'   },
            ].map(v => (
              <button key={v.id} onClick={() => setView(v.id)}
                style={{ padding: '6px 14px', border: 'none', borderRadius: 20, cursor: 'pointer', fontSize: '0.82rem',
                  fontWeight: view === v.id ? 'bold' : 'normal',
                  background: view === v.id ? '#8b5cf6' : '#f1f5f9',
                  color: view === v.id ? 'white' : '#475569' }}>
                {v.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ fontSize: 11, color: '#64748b' }}>
          <b>{parts.length}</b> working part(s) · DB has <b>{existingParts.length}</b> part(s)
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {view === 'decompose' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <AIPartDecomposer onPartsDetected={handleAiParts} />
            {existingParts.length > 0 && (
              <div style={{ background: 'white', padding: 16, borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <div style={{ fontSize: 13, fontWeight: 'bold', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FolderOpen size={16} color="#2563eb" /> Or pull in existing parts from Part DNA
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8, maxHeight: 240, overflowY: 'auto' }}>
                  {existingParts.filter(p => p.specs?.template).map(p => (
                    <button key={p.id} onClick={() => importFromPartDNA(p)}
                      style={{ padding: 8, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', textAlign: 'center' }}>
                      {p.image && <img src={p.image} alt={p.name} style={{ width: '100%', height: 70, objectFit: 'contain' }} />}
                      <div style={{ fontSize: 11, marginTop: 4, fontWeight: 'bold' }}>{p.name}</div>
                      <div style={{ fontSize: 9, color: '#64748b' }}>{p.specs?.template}</div>
                    </button>
                  ))}
                  {existingParts.filter(p => p.specs?.template).length === 0 && (
                    <div style={{ gridColumn: '1 / -1', padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 11 }}>
                      No spec-built parts in the DB yet.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {view === 'parts' && (
          <PartsList parts={parts}
            onEdit={(i) => setEditingIdx(i)}
            onDelete={deletePart}
            onAddManual={addManual}
            onPromoteToPartDNA={promoteToPartDNA} />
        )}

        {view === 'assembly' && (
          <GarmentAssembly parts={parts} garmentName={garmentName} setGarmentName={setGarmentName} onSaveAsStyle={saveAsStyle} />
        )}

        {view === 'spread' && (
          <SpreadLayoutTool parts={parts} />
        )}

        {view === 'training' && (
          <AITrainingUploader />
        )}
      </div>

      {editingIdx !== null && parts[editingIdx] && (
        <PartEditor part={parts[editingIdx]} onSave={saveEdited} onCancel={() => setEditingIdx(null)} />
      )}
    </div>
  );
}
